/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/require-await */
// @ts-strict-ignore

/**
 * @module BubbleConferences
 */

import { Subject, Subscription, filter } from "rxjs";
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Bubble, BubbleEvents, BubbleRB, } from 'rainbow-web-sdk/models/bubble.model';
import { BubbleConference, BubbleConferenceEvents, BubbleConferenceJoinParameters, BubbleConferenceMedia, BubbleConferenceMediaAction, BubbleConferenceStatus, BubbleConferenceVideoQuality, WebConferenceSession } from "rainbow-web-sdk/models/webConferenceSession.model";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { BubbleConferenceParticipant } from "rainbow-web-sdk/models/webConferenceParticipant.model";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { WebConferenceHelper } from "./webConferenceHelper";
import { WebrtcConfigAndUtils } from "rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils";
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { DeviceManagementEvents, DeviceManagementServiceRB } from "rainbow-web-sdk/services/deviceManagement/deviceManagement.service";
import type { VirtualBackgroundService } from "rainbow-web-sdk/services/virtualBackground/virtualBackground.service";
import { VIRTUAL_BACKGROUND_SVC, VIRTUAL_BACKGROUND_TYPE, VirtualBackgroundServiceEvents } from "rainbow-web-sdk/services/virtualBackground/virtualBackground.service";

import { Service } from "rainbow-web-sdk/services/service";
import type { SpeechEnhancementService } from 'rainbow-web-sdk/services/speechEnhancement/speech-enhancement.service';
import { SPEECH_ENH_SVC } from 'rainbow-web-sdk/services/speechEnhancement/speech-enhancement.service';
import { PromiseQueue } from 'rainbow-web-sdk/utils/promiseQueue';
import { $msg } from "strophe.js";
import { BubbleConferenceRecapServiceEvents, BubbleConferenceRecapServiceRB, BubbleConferenceRecapParams } from "./bubbleConferenceRecap.service";
import { BubbleConferenceRecap } from "../../models";
import { JingleSession } from "../../libs/strophe/strophe.jingle.session";
import { WebrtcStatisticsData } from "../webrtc/webrtcSessionStatsHandler";

/**
 * @internal
 */
export const BUBBLE_CONFERENCE_SVC = 'BubbleConferenceService';

/**
 * This service manages web conferences in Rainbow (web conferences that take place in bubbles).
 * Any Rainbow user in the bubble can take part of the conference (the maximum number of participants is based on the licence of the organiser of the conference)
 * In each conference, there's only one "organizer" (the user that has started the conference), but every administrator in the bubble is allowed to make additional actions on all other participants (like drop, mute, etc)
 * Only the organizer of the conference can stop the conference, or delegate it to another user.
 *
 * By default, the web conference service is going to use a web conference helper service that will manage
 * an automatic subscription to all available videos, the management of the dynamic feed.
 * This service configuration can be managed by setHelperServiceConfiguration API
 *
 * NB !! The CPU and bandwidth consumption should/can be managed via additionnal APIs that can be used so that the application is adapted;
 * As all incoming videos are available in 3 sublayers (high HD, medium VGA and low QVGA) the user can change the sublayer he wants to receive from the server (managed by the Method updateParticipant)
 * In addition, some session can be put on hold / unhold so that the user can "pause" all incoming videos that are not needed at the moment (managed by putRemoteVideoSessionOnHold)
 */

export interface BubbleConferenceService {

    /**
     * Subscribe to updates from the service (all events are of RBEvent type {name: string, data:any});
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(handler: any, eventNames?: string | string[]): Subscription;

    /**
     * Create a web conference in the given bubble
     * @param bubble - The bubble associated with this conference
     */
    startConference(bubble: Bubble): Promise<BubbleConference>;

    /**
     * Get bubble web conference if exists
     * @param bubble - The bubble associated with this conference
     */
    getConference(bubble: Bubble): BubbleConference | undefined;

    /**
     * API used to update the bubble conferences configuration
     * @param disableDynamicFeed - If true, this will disable the subscription mecanisme for the dynamic feed
     * In case the subscription is already done, this will not effect the current session;
     * NB The default value is FALSE for all conferences (i.e. by default, dynamic feed is activated)
     * @param disableAutoSubscriptions - If true, this will disable the auto subscription mecanisme for all incoming publishers (except sharing)
     * In case the subscription is already done, this will not effect the current session;
     * NB The default value is FALSE for all conferences (i.e. by default, subscription is done for all publsihers)
     */
    setBubbleConferencesConfiguration(disableDynamicFeed: boolean, disableAutoSubscriptions: boolean): void;

    /**
     * Add media (video or sharing) to an existing web conference
     * @internal
     * @param webConference - the WebConference object where we want to add the media
     * @param mediaType - the type of the media to be added. Accepted values are 'video' or 'sharing';
     * @param existingStream - if we want to use an already existing stream
     * @param getAudioForSharing - if we want to add audio from system to sharing
     */
    addMediaToWebConference(webConference: BubbleConference, mediaType: BubbleConferenceMedia, existingStream?: MediaStream, getAudioForSharing?: boolean): Promise<void>;

    /**
     * Remove Media from the current web conference. It could be either 'video' or 'sharing'
     * @internal
     * @param webConference - The Web conference
     * @param mediaType - the media to be removed 'video' or 'sharing'
     */
    removeMediaFromWebConference(webConference: BubbleConference, mediaType: BubbleConferenceMedia): void;

    /**
     *  Update a conference participant (ourself, or any other participant if the user is a moderator of the conference)
     * @internal
     * @param webConference -The Web Conference
     * @param participantId -ID of participant to update
     * @param option -update data ( could be on of: mute, unmute, hold, retrieve, plug, unplug, update);
     * With 'update' option we can :
     * - Update media bandwidth as publisher. Two parameters must be present: media and bitRate
     * - Update substream level as subscriber. One parameter must be present: subStreamLevel. Parameter publisherId is optional.
     * @param media - type of media to be updated (video/sharing)
     * @param bitRate - the max bitrate to be set (0 is unlimited)
     * @param subStreamLevel - set new sub-stream level. Only available for publishers and in simulcast mode (0 - low, 1 - medium, 2 - high)
     * @param publisherId - the publisher id related to the update
     * @returns a promise that resolve when particpant state is updated
     */
    updateParticipant(webConference: BubbleConference, participantId: string, option: string, media?: string, bitRate?: string, subStreamLevel?: string, publisherId?: string): Promise<void>;

    /**
     * Method askToAddSharingToWebConferenceSession
     * Used only on desktop application : choose which screen to share
     * FIX SIGNATURE
     * @internal
     */
    askToAddSharingToWebConferenceSession(conversation, conferenceSession, shouldAskForAudio?: boolean): void;

    /**
     * Returns WebConferenceSession if the user has an active web conference session (that he has joined)
     * @returns {WebConferenceSession} Returns WebConferenceSession or null instead
     * @internal
     * FIX SIGNATURE
     */
    getActiveWebConferenceSession(): WebConferenceSession | null;

    /**
     * Returns WebConferenceSession if the user has an unjoined web conference session owned by the user identified by displayname
     * @returns {WebConferenceSession} Returns WebConferenceSession or null instead
     * @internal
     * FIX SIGNATURE
     */
    getUnjoinedWebConferenceSessionByOwnerName(name: string): Promise<WebConferenceSession | null>;

    /**
     * Decline incoming invitation to join a web conference. This API will send update to all other connected devices to the user to notify;
     * @param bubble - The bubble to decline the web conference invitation
     * @internal
     * Returns on API call success
     */
    declineInvitationToJoinConference(bubble: Bubble): Promise<void>;

    /**
     * Removes a user from the web conference in the given bubble; This API can be used by
     * the moderators of the bubble to remove other participants too
     * @param webConferenceSession - The current web conference session
     * @param participantId - The participant ID that should be removed from the conference
     * @internal
     * @returns  Return promise
     */
    disconnectUserFromWebConference(webConferenceSession: WebConferenceSession, participantId: string): Promise<void>;

    /**
     * Add audio to an existing web conference session (ONLY IN CASE THE JOIN IS DONE VIA NOAUDIO OPTION)
     * @param webConferenceSession - the WebConferenceSession object where we want to add the media
     * @internal
     */
    addAudioToWebConferenceSession(webConferenceSession: WebConferenceSession): void;

    /**
     * API used to update the conference parameters (can be used by the conference moderator)
     * @param webConferenceSession - The Web Conference session
     * @param option - could be one of:
     * - mute  (Mutes all participants, except requester.)
     * - unmute (Unmutes all participants)
     * - lock (Disables any future participant from joining conference)
     * - unlock (Unlocks the conference)
     * @returns a promise that resolve when conference parameters are updated
     * @internal
     */
    updateConferenceParameters(webConferenceSession: WebConferenceSession, option: string): Promise<void>;

    /**
     * Method used to stop the echo test
     * @param roomDbId - the DB Id of the room where we want to get the stats
     * @internal
     */
    getConferenceTalkingTimeStats(roomDbId: string): Promise<any>;

    /**
     * Raise your hand in the concerned Conference
     * @param session - The conference session where the user is going to lower their hand
     * @returns Returns WebConferenceSession object that is related to the web conference session in that bubble
     * @internal
     */
    raiseHand(session: WebConferenceSession): Promise<WebConferenceSession>;

    /** @internal */
    putActiveConferenceOnHold(call?): any;

    /** @internal */
    retrieveConferenceOnHold(): any;

    /**
     * Method used to remove all incoming video sessions; Also the dynamic feed / active talker video is removed
     * @param webConferenceSession - The Web conference participant
     * @returns returns when it's done
     * @internal
     */
    removeAllRemoteVideoSessions(webConferenceSession: WebConferenceSession): void;

    /**
     * Method used to start the echo test
     * @internal
     */
    startMediaEchoTest(): Promise<void>;

    /**
     * Method used to stop the echo test
     * @internal
     */
    stopMediaEchoTest(): Promise<void>;

    /**
     * Method used to start a pilot test conference.
     * @returns the identifyer of the bubble used for the test
     * @internal
     */
    startMediaEchoTestV2(): Promise<string>;

    /** @internal */
    hasActiveWebConferenceSession(): boolean;
    /** @internal */
    waitWebConferenceConnection(session: BubbleConference): Promise<void>;

    /**
     * Lower your hand in the concerned conference (you can also lower the hand of someone else if you are a Bubble Organizer)
     * @param session - The conference session where the user is going to lower their hand
     * @param user - The user lowering their hand
     * @returns Returns WebConferenceSession object that is related to the web conference session in that bubble
     * @internal
     */
    lowerHand(session: WebConferenceSession, user: BubbleConferenceParticipant): Promise<WebConferenceSession>;

    /**
     * Get all conference recaps that belong to the connected user
     */
    getRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]>;

    /**
     * Get conference recaps shared with the connected user
     */
    getSharedWithMeRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]>;

    // Add to build but should be removed
    /** @internal */
    sendInnerEvent(name: string, data?: any): void;
    /** @internal */
    subscribeToInnerEvents(handler: any): Subscription;
    /** @internal */
    sendCustomEvent(name: string, data?: any): void;
    /** @internal */
    sendEvent(name: string, data?: any): void;
}

/**
 * @eventProperty
 */
export enum BubbleConferenceServiceEvents {

    /**
     * @eventProperty ON_CONFERENCE_STARTED
     * This RB event is fired when a conference has started in a bubble
     *
     * **Event Data**
     * - **bubbleConference** : The newly started webConference
     */
    ON_CONFERENCE_STARTED = "ON_CONFERENCE_STARTED",

    /**
     * @eventProperty ON_CONFERENCE_ENDED
     * This event is send when the conference in the given bubble is no longer active. This event is received after the owner has
     * stopped the conference (it could also be us)
     *
     * **Event Data**
     * - **bubbleConference** : The webConference
     */
    ON_CONFERENCE_ENDED = "ON_CONFERENCE_ENDED",

    /**
     * @eventProperty ON_CONFERENCE_RECAP_CREATED
     * @internal
     * This RB event is fired when a new recap recording is created
     */
    ON_CONFERENCE_RECAP_CREATED = "ON_CONFERENCE_RECAP_CREATED",

    /**
     * @eventProperty ON_CONFERENCE_RECAP_READY
     * @internal
     * This RB event is fired when a new recap recording is ready to be displayed
     */
    ON_CONFERENCE_RECAP_READY = "ON_CONFERENCE_RECAP_READY",

    /**
     * @eventProperty ON_CONFERENCE_RECAP_REMOVED
     * @internal
     * This RB event is fired when a new recap is removed
     */
    ON_CONFERENCE_RECAP_REMOVED = "ON_CONFERENCE_RECAP_REMOVED",

    /**
     * @eventProperty ON_CONFERENCE_RECAP_SHARED
     * @internal
     * This RB event is fired when a recap is shared
     */
    ON_CONFERENCE_RECAP_SHARED = "ON_CONFERENCE_RECAP_SHARED",

    /**
     * @eventProperty ON_CONFERENCE_STATUS_UPDATE
     * This RB event is send when the status of the current conference has changed.
     *
     * **Event Data**
     * - **bubbleConference** : The DB ID of the bubble related to the bubble conference
     * - **status** : The new status for the web conference;It could be "connecting", "connected", "reconnecting", "onhold", "ended"s
     */
    ON_CONFERENCE_STATUS_UPDATE = "ON_CONFERENCE_STATUS_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE
     * @internal
     * This RB event is when there's a modification in the list of the participants (participants are added or removed)
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE = "ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_PARTICIPANT_ADD
     * @internal
     * This RB event is when there's a new participant that has been added in the participant list / conference
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **participantId** : The DB ID of the participant that is added in the conference
     */
    ON_WEBCONFERENCE_PARTICIPANT_ADD = "ON_WEBCONFERENCE_PARTICIPANT_ADD",

    /**
     * @eventProperty ON_WEBCONFERENCE_PARTICIPANT_REMOVE
     * @internal
     * This RB event is when there's a participant that has been removed in the participant list / conference
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **participantId** : The DB ID of the participant that have been removed from the conference
     */
    ON_WEBCONFERENCE_PARTICIPANT_REMOVE = "ON_WEBCONFERENCE_PARTICIPANT_REMOVE",

    /**
     * @eventProperty ON_WEBCONFERENCE_PUBLISHER_ADD
     * @internal
     * This RB event is when there's a new video or sharing media available in the conference.
     * This could be in the list of the participants (participants have now video available)
     * Or this could be an update on the sharing session (added)
     * This event is also fired when the current user adds sharing or video
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_PUBLISHER_ADD = "ON_WEBCONFERENCE_PUBLISHER_ADD",

    /**
     * @eventProperty ON_WEBCONFERENCE_PUBLISHER_REMOVE
     * @internal
     * This RB event is when there's a video or sharing media removed from the conference.
     * This could be in the list of the participants (participants have no longer video available)
     * Or this could be an update on the sharing session (removed)
     * This event is also fired when the current user removes sharing or video
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_PUBLISHER_REMOVE = "ON_WEBCONFERENCE_PUBLISHER_REMOVE",

    /**
     * @eventProperty ON_WEBCONFERENCE_LOCKSTATE_UPDATE
     * @internal
     * This RB event is when there's an update in the lock state of the conference
     * If a conference is locked, no other user can join it (the JOIN is rejected)
     * Conference can be locked or unlocked by any moderator of the bubble / room
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **isLock** : Boolean indicating whether the conference is locked
     * - **lockedById** : The DB ID of the user that locked / unlocked the conference
     */
    ON_WEBCONFERENCE_LOCKSTATE_UPDATE = "ON_WEBCONFERENCE_LOCKSTATE_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_UNMUTE_DEMAND_RECEIVED
     * @internal
     * This RB event is received when we're asked to unmute ourselves from a distant party;
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **askedBy** : The DB ID of the user that asked ourselves to be unmuted
     */
    ON_WEBCONFERENCE_UNMUTE_DEMAND_RECEIVED = "ON_WEBCONFERENCE_UNMUTE_DEMAND_RECEIVED",

    /**
     * @eventProperty ON_WEBCONFERENCE_LOCALMEDIA_UPDATE
     * @internal
     * This RB event is when there's an update in the local medias.
     * It could be when we add media (video or sharing)
     * It could be when we remove media (video or sharing)
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **mediaType** : The type of media that is updated : 'video' or 'sharing'
     * - **action** : Could be 'added' or 'removed'
     */
    ON_WEBCONFERENCE_LOCALMEDIA_UPDATE = "ON_WEBCONFERENCE_LOCALMEDIA_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_LOCAL_PARTICIPANT_UPDATE
     * @internal
     * This RB event is send when the local participant is updated (mute, delegation, etc)
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_LOCAL_PARTICIPANT_UPDATE = "ON_WEBCONFERENCE_LOCAL_PARTICIPANT_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE
     * @internal
     * This RB event is when there's an update in the remote media sessions
     * It could be when another user adds media (video or sharing)
     * The media type is 'mozaic' in case the session is related to the Large Grid service (49 videos at once), available via WebConferenceSession.gridServiceSubscription
     * It could be when another user removes media (video or sharing)
     * NB Not the same thing as ON_WEBCONFERENCE_PUBLISHER_ADD, as this event indicates
     * that there's a publisher available, but the ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE is fired
     * when we actually subscribe to a remote media (either sharing or video) and the media can be played
     * by using attachVideoSessionToHtmlElement API
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **mediaType** : The type of media that is updated : 'video' or 'sharing' or 'mozaic' (the large grid of 49 videos) or 'dynamicFeed' (The active talker video)
     * - **action** : Could be 'added' or 'removed' or 'hold'
     */
    ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE = "ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_PARTICIPANT_UPDATE
     * @internal
     * This RB event is when there's an update in one of the participants
     * It could be when a user is muted/unmuted
     * It could be when the role or the delegation capability has changed
     * It could be when the user microphone capability has changed
     * It could be when the distant user is asked to be unmuted. In that case, participant ID and action "askToUnmute" will be sent to the event
     * It could be when a user raises/lowers their hand. In that case, participant ID and action "raise-hand" or "lower-hand" will be sent to the event
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_PARTICIPANT_UPDATE = "ON_WEBCONFERENCE_PARTICIPANT_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_TALKER_UPDATE
     * @internal
     * This RB event is when there's an update in the talker participant
     * It could be when a user starts speaking
     * It could be when the current speaker changes
     * It could be when the last speaker stops speaking (noone is speaking)
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_TALKER_UPDATE = "ON_WEBCONFERENCE_TALKER_UPDATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_DELEGATE
     * @internal
     * This RB event is fired when the conference is delegated, i.e. the owner of the conference has changed
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     * - **userDbId** : The DB ID of the user that is the new owner
     * - **gaveOwnership** : If true it means that the last owner of the conference was the "myself"
     */
    ON_WEBCONFERENCE_DELEGATE = "ON_WEBCONFERENCE_DELEGATE",

    /**
     * @eventProperty ON_WEBCONFERENCE_SERVICE_UPDATE
     * @internal
     * This RB event is when there's an update in the "conference services"
     * It's used for other services like Grid 49, etc
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble related to the web conference
     */
    ON_WEBCONFERENCE_SERVICE_UPDATE = "ON_WEBCONFERENCE_SERVICE_UPDATE",

    // FIXME: LEGACY EVENT USE THE NEW ON_CONFERENCE_STARTED SDK EVENT INSTEAD
    /**
     * @eventProperty ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE
     * @internal
     * This RB event is fired when a conference has started in a bubble/room (not by user itself)
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble where the web conference has started
     */
    ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE = "ON_WEBCONFERENCE_STARTED_EVENT_IN_BUBBLE",

    /**
     * @eventProperty ON_WEBCONFERENCE_STOP_HIGHLIGHTING
     * @internal
     * This RB event is fired when ????
     */
    ON_WEBCONFERENCE_STOP_HIGHLIGHTING = "stopHighlighting",

    /**
     * @eventProperty ON_WEBCONFERENCE_ECHOTEST_STATS_UPDATE
     * @internal
     * This RB event is fired when an echo test conference generates stats
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble where the web conference has started
     * - **stats** : The WebrtcStatisticsData received
     */
    ON_WEBCONFERENCE_ECHOTEST_STATS_UPDATE = "ON_WEBCONFERENCE_ECHOTEST_STATS_UPDATE",
}

/**
 * @internal
 */
export enum WebConferenceServiceInnerEvents {
    /**
     * @eventProperty ON_WEBCONFERENCE_HIDE_INVITATION
     * This event is send when we the user has either joined the conference in that bubble, or rejected the incoming call, or joined with other device
     * It's used so that we need to hide the incoming call for these additional cases; For end of conference, check also ON_CONFERENCE_ENDED
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble
     */
    ON_WEBCONFERENCE_HIDE_INVITATION = "ON_WEBCONFERENCE_HIDE_INVITATION",

    /**
     * @eventProperty ON_WEBCONFERENCE_SHOW_INVITATION
     * This event is send when we need to show an invitation to join the bubble conf
     *
     * **Event Data**
     * - **roomDbId** : The DB ID of the bubble
     */
    ON_WEBCONFERENCE_SHOW_INVITATION = "ON_WEBCONFERENCE_SHOW_INVITATION",

    /**
     * @eventProperty ON_CONFERENCE_UPDATE_SUPERVISION_STATE
     * This RB event is send when our participation in a conference is updated and we need to send a supervision state change to our ressources
     */
    ON_CONFERENCE_UPDATE_SUPERVISION_STATE = "ON_CONFERENCE_UPDATE_SUPERVISION_STATE",
}

/**
 * @internal
 */
export class BubbleConferenceServiceRB extends Service implements BubbleConferenceService {

    public webConferenceSessions: Record<string, WebConferenceSession> = {};
    public webConferenceHelper: WebConferenceHelper;
    public webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();

    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB = DeviceManagementServiceRB.getInstance();
    private virtualBackgroundService?: VirtualBackgroundService;
    private bubbleConferenceRecapService?: BubbleConferenceRecapServiceRB = BubbleConferenceRecapServiceRB.getInstance();

    public speechEnhancementService?: SpeechEnhancementService;

    private rxSubject: Subject<any> = new Subject<any>();
    private innerRxSubject: Subject<RBEvent> = new Subject<RBEvent>();
    private conferenceRef: any = null;
    private xmppJingleListener: any = null;
    private makingCall: boolean = false;
    private addingMedia: boolean = false;
    private connected: boolean = false;
    private xmppServiceSubscription: Subscription = null;
    private xmppConnectionSubscription: Subscription = null;
    private subscriptions: Subscription[] = [];
    private eventServiceSubscriptions: any[] = [];
    private statsIntervals: any[] = [];
    private reconnectionsInProgress: any = {};
    private audioProfileChanging: boolean = false;
    private promiseInProgress: [] = [];
    private nameSpaceJingle = "urn:xmpp:jingle-message:0";
    private promiseQueue: PromiseQueue = PromiseQueue.create();
    private raiseHandTemp: any[] = [];

    public adhocConferenceStarting: boolean = false;

    public static getInstance() { return Service.create(BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceRB); }
    public static build() { return new BubbleConferenceServiceRB(); }

    private constructor() { super(); }

    public async start(): Promise<void> {
        if (this.contactService.userContact && !this.contactService.userContact && !this.contactService.userContact.roomsAsVoiceEnabled) {
            this.logger.info(`[WebConferenceService] === start skipped - rooms are disabled for this contact`);
            return;
        }

        this.virtualBackgroundService = this.mainService.inject<VirtualBackgroundService>(VIRTUAL_BACKGROUND_SVC);
        this.speechEnhancementService = this.mainService.inject<SpeechEnhancementService>(SPEECH_ENH_SVC);

        this.attachHandlers();
        this.webConferenceHelper = new WebConferenceHelper(this);
        this.webConferenceHelper.start();

        this.connected = true;
    }

    public async stop(): Promise<void> {
        while (this.statsIntervals.length) {
            let statsInterval = this.statsIntervals.pop();
            if (statsInterval) { clearInterval(statsInterval); statsInterval = null; }
        }

        this.eventServiceSubscriptions.forEach(listener => listener());
        this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        this.eventServiceSubscriptions = [];
        this.subscriptions = [];

        if (this.conferenceRef) { this.xmppService.connection.deleteHandler(this.conferenceRef); this.conferenceRef = null; }
        if (this.xmppJingleListener) { this.xmppService.connection.deleteHandler(this.xmppJingleListener); this.xmppJingleListener = null; }
        if (this.xmppServiceSubscription) { this.xmppServiceSubscription.unsubscribe(); this.xmppServiceSubscription = null; }
        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); this.xmppConnectionSubscription = null; }
        if (this.webConferenceHelper) { this.webConferenceHelper.stop(); this.webConferenceHelper = null; }

        // Stop and remove all webconference sessions
        const webConferenceSession = this.getActiveWebConferenceSession();
        if (webConferenceSession) {
            webConferenceSession.status = BubbleConferenceStatus.ENDED;
            webConferenceSession.metricsState = "ended";
            webConferenceSession.haveJoined = false;
            this.disableAllLocalStreamsForSession(webConferenceSession);
            webConferenceSession.audioSession = null;
            webConferenceSession.localParticipant = null;
            delete this.webConferenceSessions[webConferenceSession.id];

            // Send event for desktop app to stop highlight
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STOP_HIGHLIGHTING);
        }

        this.adhocConferenceStarting = false;
    }

    private attachXmppHandlers() {
        if (this.conferenceRef) {
            this.xmppService.connection.deleteHandler(this.conferenceRef);
            this.conferenceRef = null;
        }

        if (this.xmppJingleListener) { this.xmppService.deleteHandler(this.xmppJingleListener); this.xmppJingleListener = null; }
        this.xmppJingleListener = this.xmppService.addHandler((stanza: any) => { this.onXmppJingleMessage(stanza); return true; }, this.nameSpaceJingle, 'message', null);

        this.conferenceRef = this.xmppService.connection.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onConferenceMessage(stanza)); return true; }, "jabber:iq:conference:2", "message", null);

        if (this.xmppServiceSubscription) { this.xmppServiceSubscription.unsubscribe(); this.xmppServiceSubscription = null; }
        this.xmppServiceSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((event: RBEvent) => {
            try { this.onJingleMessage(event); }
            catch (error) { this.logger.error(`[WebConferenceService] onJingleMessage error -- ${error}`); }
        });
    }

    public subscribe(handler: any, eventNames?: string | string[]): Subscription {
        const sdkObservables = this.rxSubject.pipe(filter((event: RBEvent) => {
            switch (event.name) {
                case BubbleConferenceServiceEvents.ON_CONFERENCE_STARTED:
                case BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED:
                case BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_CREATED:
                case BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_READY:
                case BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_REMOVED:
                case BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_SHARED:
                case BubbleConferenceServiceEvents.ON_WEBCONFERENCE_ECHOTEST_STATS_UPDATE:
                    return true;
                default: return false;
            }
        }));

        const observable = eventNames
            ? sdkObservables.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : sdkObservables;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[WebConferenceService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public eventSubscribe(handler: any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[WebConferenceService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendCustomEvent(name: string, data: any = null): void {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    public sendEvent(name: string, data: any = null): void {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /**
     * Subscribe to inner events for the web conference sessions (all events are of RBEvent type {name: string, data:any});
     * Most of the events are not documentated, and they're usually not to be used by external app;
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @returns Returns RxJS Subscription
     */
    public subscribeToInnerEvents(handler): Subscription {
        return this.innerRxSubject.subscribe((event: RBEvent) => { try { handler(event); } catch { } });
    }

    public sendInnerEvent(name: string, data: any = null) {
        this.innerRxSubject.next(RBEvent.create(name, data));
    }

    // ****************************************************************** //
    // ** PUBLIC API                                                   ** //
    // ****************************************************************** //

    public async startConference(bubble: Bubble): Promise<BubbleConference> {
        try {
            if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED))
                throw Error("[WebConferenceService] createWebConference -- User does not have the profile feature WEBRTC_CONFERENCE_ALLOWED");

            const params: any = { lounge: true };
            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_GRID49) || this.settingsService.getBooleanSetting("grid49")) {
                this.logger.info("[WebConferenceService] createWebConference -- activate grid 49");
                params.services = ['video-compositor'];
            }

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }
            webConferenceSession.isMyConference = true;

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/start`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;

            this.logger.info(`[WebConferenceService] createWebConference (id: ${webConferenceSession.id}) -- successfully`);

            return webConferenceSession;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            delete this.webConferenceSessions[bubble.dbId];
            throw errorInfo.error;
        }
    }

    public getConference(bubble: Bubble): BubbleConference | undefined {
        let webConference = this.getWebConferenceSessionById(bubble.dbId);
        if (webConference) return webConference;
        else if (bubble.isBubbleConferenceStarted()) {
            webConference = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
            webConference.status = BubbleConferenceStatus.UNJOINED;
            if (bubble.getBubbleConferenceOwnerId() === this.contactService.userContact.dbId) webConference.isMyConference = true;
            this.webConferenceSessions[bubble.dbId] = webConference;
            return webConference;
        }
        return undefined;
    }

    /**
     * Start a web conference in the given bubble
     * @param bubble - The bubble where the user is going to start the conference
     * @param recordConfig - Allows to start recording immediately when the conference begins with the specified params.
     * @returns Returns WebConference object that is related to the web conference session in that bubble
     */
    public async startWebConference(bubble: Bubble, recordConfig: { params: BubbleConferenceRecapParams; saveConfig: boolean; } = null): Promise<BubbleConference> {

        if (!bubble) { throw Error("Wrong or missing parameters"); }

        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED)) {
            throw Error("[WebConferenceService] startWebConference -- User does not have the profile feature WEBRTC_CONFERENCE_ALLOWED");
        }

        try {
            this.logger.debug("[WebConferenceService] startWebConference -- " + bubble.dbId);

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            if (!webConferenceSession.localParticipant) {
                webConferenceSession.localParticipant = BubbleConferenceParticipant.create(this.contactService.userContact.dbId);
                webConferenceSession.localParticipant.contact = this.contactService.userContact;
            }

            webConferenceSession.isMyConference = true;
            webConferenceSession.haveJoined = true;
            if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                let data: { bubbleConference: WebConferenceSession; status: BubbleConferenceStatus; recordConfig?: { params: BubbleConferenceRecapParams; saveConfig: boolean; }; } = { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.CONNECTING };
                if (recordConfig)
                    data = { ...data, recordConfig: recordConfig };
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, data);
            }

            const params: any = {};
            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_GRID49) || this.settingsService.getBooleanSetting("grid49")) {
                this.logger.info("[WebConferenceService] startWebConference -- activate grid 49");
                params.services = ['video-compositor'];
            }
            if (this.contactService.userContact.canLiveTranscription && (this.settingsService.getBooleanSetting("liveTranscription") || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.LIVE_CAPTION))) {
                this.logger.info("[WebConferenceService] startWebConference -- activate liveTranscription");
                params.liveTranscription = true;
            }

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/start`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;

            this.logger.info("[WebConferenceService] startWebConference -- successfully");
        }
        catch (error) {
            this.resetBusyStateIfNeeded();
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            this.webConferenceSessions[bubble.dbId].haveJoined = false;
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });

            delete this.webConferenceSessions[bubble.dbId];

            this.mainService.showGlobalPopupMessage(data);
            throw errorInfo.error;
        }

        return this.joinWebConference(bubble);
    }

    /**
     * Start a web conference in the given bubble with the audio being available on the deskphone
     * @param bubble - The bubble where the user is going to start the conference
     * @returns Returns WebConference object that is related to the web conference session in that bubble
     */
    public async startWebConferenceByPhone(bubble: Bubble): Promise<BubbleConference> {

        if (!bubble) { throw Error("Wrong or missing parameters"); }

        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED)) {
            throw Error("[WebConferenceService] startWebConferenceByPhone -- User does not have the profile feature WEBRTC_CONFERENCE_ALLOWED");
        }

        try {
            this.logger.debug("[WebConferenceService] startWebConferenceByPhone -- " + bubble.dbId);

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            webConferenceSession.isMyConference = true;
            if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.CONNECTING });
            }

            const params: any = {};
            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_GRID49) || this.settingsService.getBooleanSetting("grid49")) {
                this.logger.info("[WebConferenceService] startWebConferenceByPhone -- activate grid 49");
                params.services = ['video-compositor'];
            }
            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/start`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] startWebConferenceByPhone -- successfully");

            return this.joinWebConferenceByPhone(bubble);
        }
        catch (error) {
            this.resetBusyStateIfNeeded();
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            this.webConferenceSessions[bubble.dbId].haveJoined = false;
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });

            delete this.webConferenceSessions[bubble.dbId];

            this.mainService.showGlobalPopupMessage(data);
            throw errorInfo.error;
        }
    }

    /**
     * Stop a web conference in the given bubble
     * @param bubble - The bubble where the user wants to end the conference
     */
    public async stopWebConference(bubble: Bubble): Promise<void> {
        try {
            this.logger.debug("[WebConferenceService] stopWebConference");
            if (!bubble) { throw Error("Wrong or missing parameters"); }
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/stop`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] stopWebConference -- successfully");
            if (this.webConferenceSessions[bubble.dbId]) {
                this.removeActiveConferenceSession(this.webConferenceSessions[bubble.dbId]);
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'stopWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Joins a web conference in the given bubble
     * @param bubble - The bubble where the user wants to join a conference (there should be an active conference in the bubble)
     * @param joinParams - The join conference parameters
     * @returns  Returns WebConferenceSession object that is related to the web conference session in that bubble
     */
    public async joinWebConference(bubble: Bubble, joinParams?: BubbleConferenceJoinParameters): Promise<BubbleConference> {
        try {
            if (this.makingCall) {
                this.logger.warn("[WebConferenceService] joinWebConference -- already joining a conference");
                return this.webConferenceSessions[bubble.dbId];
            }
            if (!bubble) { throw Error("Wrong or missing parameters"); }

            // Use new method to make join without audio
            if (joinParams?.noAudio) {
                await this.joinWebConferenceWithoutAudio(bubble as BubbleRB);
                return this.webConferenceSessions[bubble.dbId];
            }

            this.logger.debug("[WebConferenceService] joinWebConference -- " + bubble.dbId);

            this.makingCall = true;

            this.contactService.setBusyState("dnd", "audio");

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            if (!webConferenceSession.localParticipant) {
                webConferenceSession.localParticipant = BubbleConferenceParticipant.create(this.contactService.userContact.dbId);
                webConferenceSession.localParticipant.contact = this.contactService.userContact;
            }

            if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.CONNECTING });
            }

            const id = "web_" + crypto.randomUUID();
            const resourceId = this.xmppService.getResourceFromJid(this.xmppService.fullJid);
            const params: any = { mute: false, resourceId, media: ['audio', 'video', 'application'] };

            if (joinParams?.joinMultiple) {
                params.joinMultiple = true;
            }

            if (this.webrtcConfigAndUtils?.webrtcConfig?.dc) {
                params.dc = this.webrtcConfigAndUtils.webrtcConfig.dc;
            }

            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.UNMUTE_REQUEST)) {
                params.askToUnmute = true;
            }

            let stream = (joinParams?.attendeeMode) ? await this.getSilentStream() : await this.getUserMediaForSession(bubble.dbId);
            if (stream === "noMic") {
                params.microphone = false;
                webConferenceSession.localStreams = [];
            }
            else {
                if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.speechEnhancementService.startProcessStream(bubble.dbId, stream, "[WebConferenceService] joinWebConference");
                }
                webConferenceSession.localStreams.push(stream);
            }

            webConferenceSession.haveJoined = true;

            //in case we should pop-out the conf, send event here after we've taken the media from the conf
            //only do it when it's join action, and not start
            if (this.settingsService.getBooleanSetting("openPopoutOnAnswerCall") && !webConferenceSession.isMyConference) {
                this.sendInnerEvent('CONFERENCE_AUTO_POPOUT', { roomDbId: bubble.dbId });
            }

            const codecs = this.manageCodecs();
            params.codecs = codecs;

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/join`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] joinWebConference -- successfully");
            this.makingCall = false;
            const jingleJid = data.jingleJid;
            const forceTurn = data.transportTurn;
            const transcription = data.liveTranscription;
            if (transcription) {
                webConferenceSession.liveTranscriptionAvailable = true;
                webConferenceSession.liveTranscriptionLanguages = transcription.availableLanguages;
            }

            webConferenceSession.jingleJid = jingleJid;
            await this.initJingleCall(jingleJid, bubble.dbId, id, forceTurn);

            // Check if the conference owner is me, then this is case of switch device
            const confOnwerId = bubble.getBubbleConferenceOwnerId();
            if (!webConferenceSession.isMyConference && confOnwerId && confOnwerId === this.contactService.userContact.dbId) {
                webConferenceSession.isMyConference = true;
            }

            this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_CONFERENCE_UPDATE_SUPERVISION_STATE);

            return webConferenceSession;
        }
        catch (error) {
            this.makingCall = false;
            this.webConferenceSessions[bubble.dbId].haveJoined = false;
            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            // this.webConferenceSessions[bubble.dbId].sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            this.removeActiveConferenceSession(this.webConferenceSessions[bubble.dbId]);

            this.resetBusyStateIfNeeded();

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403615) {
                data.popupBody = "maximumNumberConferenceParticipantsReach";
            }

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403060) {
                data.popupBody = "lockedConferenceError";
            }

            this.mainService.showGlobalPopupMessage(data);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });
            delete this.webConferenceSessions[bubble.dbId];

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403068) {
                //the conference no longer exists for this room, clear all data
                if (bubble.isBubbleConferenceStarted()) {
                    (bubble as BubbleRB).conference.sessions = [];
                }
                bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);
            }

            throw errorInfo.error;
        }
    }

    /**
     * Joins a web conference in the given bubble by his deskphone (only for RB Hub users); The user will also join the bubble in "no audio mode"
     * @param bubble - The bubble where the user wants to join a conference (there should be an active conference in the bubble)
     * @returns Returns WebConference object that is related to the web conference session in that bubble.
     */
    public async joinWebConferenceByPhone(bubble: Bubble): Promise<BubbleConference> {
        try {
            if (this.makingCall) {
                this.logger.warn("[WebConferenceService] joinWebConferenceByPhone -- already joining a conference");
                throw Error("Joining web conference in progress");
            }
            if (!bubble) { throw Error("Wrong or missing parameters"); }

            this.logger.debug("[WebConferenceService] joinWebConferenceByPhone -- " + bubble.dbId);

            if (!this.webConferenceSessions[bubble.dbId]) {
                const webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            if (this.webConferenceSessions[bubble.dbId].status !== BubbleConferenceStatus.CONNECTING) {
                this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.CONNECTING;
                // this.webConferenceSessions[bubble.dbId].sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.CONNECTING });
            }

            // const id = "web_" + uuid4();
            // const params = { mute: false };
            const params = { deskphone: true };

            // if (this.webrtcConfigAndUtils && this.webrtcConfigAndUtils.webrtcConfig && this.webrtcConfigAndUtils.webrtcConfig.dc) {
            //     params["dc"] = this.webrtcConfigAndUtils.webrtcConfig.dc;
            // }

            // params["microphone"] = false;
            // params["deskphone"] = true;
            this.webConferenceSessions[bubble.dbId].localStreams = [];
            this.webConferenceSessions[bubble.dbId].haveJoined = true;

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/join`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] joinWebConferenceByPhone -- successfully");

            const conferenceCallNumber: string = String(data.gatewayPrefix) + String(data.gatewaySessionId);

            if (this.contactService.userContact && this.contactService.userContact.systDeviceName === Contact.SIPWISE_RVCP) {
                // Sip Wise usecase: send event for the deskphone to call the number
                this.eventService.publish("TELEPHONY_MAKE_SIMPLE_CALL_TO_NUMBER", conferenceCallNumber, bubble);
            } else {
                // Hybrid usecase: we must not send a makecall request, because the server calls my user after the join request
                this.eventService.publish("TELEPHONY_AUTOANSWER_CONFERENCECALL", String(data.callerPhoneNumber), bubble);
            }

            await this.joinWebConferenceWithoutAudio(bubble as BubbleRB, true);

            return this.webConferenceSessions[bubble.dbId];
        }
        catch (error) {
            this.makingCall = false;
            if (this.webConferenceSessions[bubble.dbId]) {
                this.webConferenceSessions[bubble.dbId].haveJoined = false;
            }

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            this.removeActiveConferenceSession(this.webConferenceSessions[bubble.dbId]);

            this.resetBusyStateIfNeeded();

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            if (errorInfo?.error) {
                if (errorInfo.error.errorDetailsCode === 403615) { data.popupBody = "maximumNumberConferenceParticipantsReach"; }
                else if (errorInfo.error.errorDetailsCode === 403620) { data.popupBody = "maximumNumberConferenceParticipantsReach"; }
                else if (errorInfo.error.errorDetailsCode === 403060) { data.popupBody = "lockedConferenceError"; }
                else if (errorInfo.error.errorDetailsCode === 403082) { data.popupBody = "switchDeviceConfError"; }
            }

            this.mainService.showGlobalPopupMessage(data);
            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            // this.webConferenceSessions[bubble.dbId].sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });
            delete this.webConferenceSessions[bubble.dbId];
            throw errorInfo.error;
        }
    }

    /**
     * Joins a web conference for media echo test
     * @param bubble - The bubble where the user wants to join a conference (there should be an active conference in the bubble)
     * @param joinParams - The join conference parameters
     * @returns  Returns WebConferenceSession object that is related to the web conference session in that bubble
     */
    public async joinEchoTestWebConference(bubble: Bubble, joinParams?: BubbleConferenceJoinParameters): Promise<BubbleConference> {
        try {
            if (this.makingCall) {
                this.logger.warn("[WebConferenceService] joinWebConference -- already joining a conference");
                return this.webConferenceSessions[bubble.dbId];
            }
            if (!bubble) { throw Error("Wrong or missing parameters"); }

            this.logger.debug("[WebConferenceService] joinEchoTestWebConference -- " + bubble.dbId);

            this.makingCall = true;

            this.contactService.setBusyState("dnd", "audio");

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            if (!webConferenceSession.localParticipant) {
                webConferenceSession.localParticipant = BubbleConferenceParticipant.create(this.contactService.userContact.dbId);
                webConferenceSession.localParticipant.contact = this.contactService.userContact;
            }

            if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.CONNECTING });
            }

            const id = "web_" + crypto.randomUUID();
            const resourceId = this.xmppService.getResourceFromJid(this.xmppService.fullJid);
            const params: any = { mute: false, resourceId, media: ['audio', 'video', 'application'] };

            if (this.webrtcConfigAndUtils?.webrtcConfig?.dc) {
                params.dc = this.webrtcConfigAndUtils.webrtcConfig.dc;
            }

            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.UNMUTE_REQUEST)) {
                params.askToUnmute = true;
            }

            const stream = await this.webrtcConfigAndUtils.getBrowserMedia(["audio", "video"]);
            webConferenceSession.localStreams.push(stream);

            webConferenceSession.haveJoined = true;

            const codecs = this.manageCodecs();
            params.codecs = codecs;

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/join`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] joinWebConference -- successfully");
            this.makingCall = false;
            const jingleJid = data.jingleJid;

            webConferenceSession.jingleJid = jingleJid;
            const sid = Math.random().toString(36).slice(2, 12);
            const session = await this.xmppService.connection.jingle.initiateBundleSession(jingleJid, this.xmppService.connection.jid, sid, stream, bubble.dbId);

            // this.statsIntervals.push(session.getStats(3000));
            this.statsIntervals.push(this.webrtcConfigAndUtils.getStatsForSession(session, 4000));

            // Check if the conference owner is me, then this is case of switch device
            const confOnwerId = bubble.getBubbleConferenceOwnerId();
            if (!webConferenceSession.isMyConference && confOnwerId && confOnwerId === this.contactService.userContact.dbId) {
                webConferenceSession.isMyConference = true;
            }

            // Specify that this is a media echo test session
            webConferenceSession.isMediaEchoTestConference = true;

            this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_CONFERENCE_UPDATE_SUPERVISION_STATE);

            return webConferenceSession;
        }
        catch (error) {
            this.makingCall = false;
            this.webConferenceSessions[bubble.dbId].haveJoined = false;
            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            // this.webConferenceSessions[bubble.dbId].sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            this.removeActiveConferenceSession(this.webConferenceSessions[bubble.dbId]);

            this.resetBusyStateIfNeeded();

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403615) {
                data.popupBody = "maximumNumberConferenceParticipantsReach";
            }

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403060) {
                data.popupBody = "lockedConferenceError";
            }

            this.mainService.showGlobalPopupMessage(data);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });
            delete this.webConferenceSessions[bubble.dbId];

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403068) {
                //the conference no longer exists for this room, clear all data
                if (bubble.isBubbleConferenceStarted()) {
                    (bubble as BubbleRB).conference.sessions = [];
                }
                bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);
            }

            throw errorInfo.error;
        }
    }

    /**
     * Removes me from the current conference. A "shortcut" for API disconnectUserFromWebConference
     * @param webConference - The current web conference
     */
    public async leaveWebConference(webConference: BubbleConference): Promise<void> {
        try {
            if (!webConference) { throw Error(" Wrong or missing parameters"); }
            else {
                if (!webConference.localParticipant) this.removeActiveConferenceSession(this.webConferenceSessions[webConference.id]);
                else await this.disconnectUserFromWebConference(webConference as WebConferenceSession, webConference.localParticipant.id);
            }
        }
        catch (error) { throw Error(`[WebConferenceService] leaveWebConference error -- ${error}`); }
    }

    /**
     * Add media (video or sharing) to an existing web conference
     * @param webConference - the WebConference object where we want to add the media
     * @param mediaType - the type of the media to be added. Accepted values are 'video' or 'sharing';
     * @param existingStream - if we want to use an already existing stream
     * @param getAudioForSharing - if we want to add audio from system to sharing
     */
    public async addMediaToWebConference(webConference: BubbleConference, mediaType: BubbleConferenceMedia, existingStream: MediaStream = null, getAudioForSharing: boolean = false): Promise<void> {
        try {
            if (!webConference) { throw Error("Wrong or missing parameters"); }
            if (this.addingMedia) { throw Error("Already adding media"); }

            this.addingMedia = true;
            const mediaToGet = [mediaType];

            if (mediaType === BubbleConferenceMedia.SHARING) {
                this.contactService.setBusyState("dnd", "sharing");
            }

            this.logger.info("[WebConferenceService] addMediaToWebConference to session " + webConference.id + " with media " + mediaType);
            const stream = existingStream ? existingStream : await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, undefined, undefined, undefined, undefined, getAudioForSharing);

            const webConferenceSession = webConference as WebConferenceSession;
            let modifiedStream = stream;
            if (mediaType === BubbleConferenceMedia.VIDEO) {
                webConferenceSession.originalVideoStream = stream;
                if (!this.virtualBackgroundService) modifiedStream = stream;
                else modifiedStream = await this.virtualBackgroundService.startProcessStream(stream);
            }
            this.addingMedia = false;
            const streams = [modifiedStream];
            const session = await this.xmppService.connection.jingle.initiate(webConferenceSession.jingleJid, this.xmppService.connection.jid, mediaType, streams, null, webConferenceSession.id, null);

            if (mediaType === BubbleConferenceMedia.VIDEO) {
                webConferenceSession.localParticipant.isVideoAvailable = true;
                webConferenceSession.localParticipant.videoSession = session;
            }
            else {
                const sharingParticipant = new BubbleConferenceParticipant(webConferenceSession.localParticipant.id);
                sharingParticipant.contact = webConferenceSession.localParticipant.contact;
                sharingParticipant.isSharingParticipant = true;

                sharingParticipant.videoSession = session;
                webConferenceSession.setSharingParticipant(sharingParticipant);

                webConferenceSession.hasLocalSharing = true;
                webConferenceSession.localSharingSession = session;
                webConferenceSession.updatePinnedParticipant(webConferenceSession.getSharingParticipant(), "sharing");
                this.sendEvent("startHighlighting");
            }
            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: mediaType, action: BubbleConferenceMediaAction.ADDED });
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: mediaType, action: BubbleConferenceMediaAction.ADDED });

            //start getting stats for the session
            this.webrtcConfigAndUtils.callsStats[session.sid] = {};
            this.updateStatisticsForSession(webConferenceSession, session.sid);
            // this.statsIntervals.push(session.getStats(8000));
            this.statsIntervals.push(this.webrtcConfigAndUtils.getStatsForSession(session, 8000));
        }
        catch (error) {
            this.addingMedia = false;
        }
    }

    /**
     * Remove Media from the current web conference. It could be either 'video' or 'sharing'
     * @param webConference - The Web conference
     * @param mediaType - the media to be removed 'video' or 'sharing'
     */
    public removeMediaFromWebConference(webConference: BubbleConference, mediaType: BubbleConferenceMedia) {
        if (!webConference) { throw Error("Wrong or missing parameters"); }
        const webConferenceSession = webConference as WebConferenceSession;
        this.logger.info("[WebConferenceService] removeMediaFromConferenceSession from session " + webConference.id);

        const session = (mediaType === BubbleConferenceMedia.VIDEO) ? webConference.localParticipant.videoSession : webConferenceSession.getLocalSharingSession();

        if (session) {
            const sessionId = session.sid;
            this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
            this.xmppService.connection.jingle.terminate(sessionId);
            //update the stats for the session before removing
            this.updateStatisticsForSession(webConferenceSession, sessionId);
            delete this.webrtcConfigAndUtils.callsStats[sessionId];
        }

        if (mediaType === BubbleConferenceMedia.VIDEO) {
            webConference.localParticipant.videoSession = null;
            webConference.localParticipant.isVideoAvailable = false;
            if (webConferenceSession.originalVideoStream) {
                this.disableMediaStream(webConferenceSession.originalVideoStream);
            }
            webConferenceSession.originalVideoStream = null;
            this.virtualBackgroundService?.stopProcessStream();
        }
        else if (mediaType === BubbleConferenceMedia.SHARING) {
            webConferenceSession.localSharingSession = null;
            webConferenceSession.hasLocalSharing = false;
            webConferenceSession.setSharingParticipant(null);
            this.contactService.setBusyState("dnd", "audio");
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STOP_HIGHLIGHTING);
        }

        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: mediaType, action: BubbleConferenceMediaAction.REMOVED });
        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConference.id, mediaType, action: BubbleConferenceMediaAction.REMOVED });
    }

    /**
     *  Update a conference participant (ourself, or any other participant if the user is a moderator of the conference)
     * @param webConference -The Web Conference
     * @param participantId -ID of particpant to update
     * @param option -update data ( could be on of: mute, unmute, hold, retrieve, plug, unplug, update);
     * With 'update' option we can :
     * - Update media bandwidth as publisher. Two parameters must be present: media and bitRate
     * - Update substream level as subscriber. One parameter must be present: subStreamLevel. Parameter publisherId is optional.
     * @param media - type of media to be updated (video/sharing)
     * @param bitRate - the max bitrate to be set (0 is unlimited)
     * @param subStreamLevel - set new sub-stream level. Only available for publishers and in simulcast mode (0 - low, 1 - medium, 2 - high)
     * @param publisherId - the publisher id related to the update
     * @returns a promise that resolve when particpant state is updated
     */
    public async updateParticipant(webConference: BubbleConference, participantId: string, option: string, media: string = null, bitRate: string = null, subStreamLevel = null, publisherId = null): Promise<void> {
        const webConferenceSession = webConference as WebConferenceSession;
        try {
            this.logger.info("[WebConferenceService] updateParticipant( webConfId=" + webConferenceSession.id + "participantId=" + participantId + " option=" + option + " subStreamLevel " + subStreamLevel + " publisherId " + publisherId + ")");
            if (!webConferenceSession || !participantId || !option) {
                throw new Error('[WebConferenceService] updateParticipant - missing a parameter');
            }

            //manage the case of PSTN external participant
            let participant = webConferenceSession.getParticipantById(participantId);

            if (!participant) {
                //try to find the participant by contact DB ID (in case of PSTN resolution)
                participant = webConferenceSession.findParticipantByContactDbId(participantId);
            }

            if (participant?.isPstnParticipant) {
                await this.updatePstnParticipant(webConferenceSession, participant.id, option);
                return;
            }

            //check if we try to mute/unmute ourselves
            if (participantId === this.contactService.userContact.dbId) {
                //in case of mute, first mute the local button and update the UI
                if (option === "mute") {
                    webConferenceSession.getAudioSession()?.muteAudio(true);
                    webConferenceSession.localParticipant.mute = true;
                }
                else if (option === "unmute") {
                    webConferenceSession.getAudioSession()?.muteAudio(false);
                    webConferenceSession.localParticipant.mute = false;
                }
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_MUTE_CHANGE, { participant: webConferenceSession.localParticipant });
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });
            }

            const params: any = { option: option };
            if (media) { params.media = media; }
            if (bitRate) { params.bitRate = bitRate; }
            if (publisherId) { params.publisherId = publisherId; }
            if (subStreamLevel) { params.subStreamLevel = subStreamLevel; }

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${participant.id}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            if (data?.askToUnmute) {
                //other user has received demand to unmute himself
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id, participantId: participant.id, action: "askToUnmute" });
            }

            if (participantId === this.contactService.userContact.dbId && option === "mute") {
                //in case of mute, remove the local mute, as this has issues with the detection while talking
                webConferenceSession.getAudioSession()?.muteAudio(false);
            }

            this.logger.info("[WebConferenceService] updateParticipant -- successfully ( webConfId=" + webConferenceSession.id + "participantId=" + participant.id + " option=" + option + " subStreamLevel " + subStreamLevel + " publisherId " + publisherId + ")");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateParticipant');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            //in case of error on mute, unmute locally and send error message
            if (participantId === this.contactService.userContact.dbId) {

                //check if user is still connected to the web conference session, otherwise ignore
                if (!this.getActiveWebConferenceSession()) return;

                if (option === "mute") {
                    webConferenceSession.getAudioSession()?.muteAudio(false);
                    webConferenceSession.localParticipant.mute = false;
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });

                    //send error message
                    const data = {
                        popupTitle: "conference",
                        popupBody: "muteConferenceError",
                        okLabel: "ok"
                    };

                    this.mainService.showGlobalPopupMessage(data);
                }
                else if (option === "unmute") {
                    //send error message
                    const data = {
                        popupTitle: "conference",
                        popupBody: "unmuteConferenceError",
                        okLabel: "ok"
                    };

                    this.mainService.showGlobalPopupMessage(data);
                }
            }
            throw errorInfo.error;
        }
    }

    // ****************************************************************** //
    // ** PRIVATE API                                                   ** //
    // ****************************************************************** //

    public sendWebConferenceEvent(name: BubbleConferenceServiceEvents, bubble: Bubble): void {
        let bubbleConference = this.getWebConferenceSessionById(bubble.dbId);
        if (name === BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED) {
            if (!bubbleConference) bubbleConference = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
            bubbleConference.status = BubbleConferenceStatus.ENDED;
        }
        this.sendEvent(name, { bubbleConference: bubbleConference });

        //for SDK, remove the conference object if any
        if (name === BubbleConferenceServiceEvents.ON_CONFERENCE_ENDED && config.sdk) {
            this.removeActiveConferenceSession(bubbleConference, true);
        }
    }

    /**
     * Attach the video session to a given HTML <video> tag element (by id)
     * @param videoElementId - the ID of the HTML video tag element
     * @param videoSession - the videoSession object that is inside the WebConferenceParticipant object
     */
    public attachVideoSessionToHtmlElement(videoElementId: string, videoSession: any): void {
        if (!videoElementId || !videoSession) { throw Error("attachVideoSessionToHtmlElement -- missing or wrong paramerets"); }
        this.webrtcConfigAndUtils.RTC.attachSessionMediaToElement(videoElementId, videoSession);
    }

    /**
     * Detach all stream from an HTML <video> tag element (by id)
     * @param videoElementId - the ID of the HTML video tag element
     */
    public detachMediaToHtmlElement(videoElementId: string): void {
        const videoElement = document.getElementById(videoElementId);
        this.webrtcConfigAndUtils.RTC.clearMediaStream([videoElement]);
    }

    /**
     * Get the owner contact for the webConference session
     * @param webConferenceSession - the ID of the HTML video tag element (should be found by document.getElementById)
     * @returns the Contact that is the "owner" / leader of the web conference
     */
    public getConferenceLeaderContact(webConferenceSession: WebConferenceSession): Contact {
        if (!webConferenceSession?.bubble) { throw Error("getConferenceLeaderContact -- missing or wrong parameters"); }
        const dbId = webConferenceSession.bubble.getBubbleConferenceOwnerId();
        let contact;
        if (dbId) {
            contact = this.contactService.getContactFromCacheById(dbId);
        }
        return contact;
    }

    /**
     * API used to update the bubble conferences configuration
     * @param disableDynamicFeed - If true, the webConferenceHelper will disable the subscription mecanisme for the dynamic feed
     * In case the subscription is already done, this will not effect the current session;
     * NB The default value is FALSE for all conferences (i.e. by default, dynamic feed is activated)
     * @param disableAutoSubscriptions - If true, the webConferenceHelper will disable the auto subscription mecanisme for all incoming publishers (except sharing)
     * In case the subscription is already done, this will not effect the current session;
     * NB The default value is FALSE for all conferences (i.e. by default, subscription is done for all publsihers)
     */
    public setBubbleConferencesConfiguration(disableDynamicFeed: boolean = false, disableAutoSubscriptions: boolean = false): void {
        this.webConferenceHelper.setConfiguration(disableDynamicFeed, disableAutoSubscriptions);
    }

    private attachHandlers() {
        this.logger.info("[WebConferenceService] attachHandlers");

        this.attachXmppHandlers();

        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); this.xmppConnectionSubscription = null; }
        this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            try {
                if (event.name === 'ON_XMPP_DISCONNECTED') {
                    this.logger.info("[WebConferenceService] onConnectionStateChangeEvent : disconnected");
                    this.connected = false;
                }
                else if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason === 'RECONNECTION') {
                    this.logger.info("[WebConferenceService] onConnectionStateChangeEvent : reconnected");
                    this.attachXmppHandlers();
                    this.connected = true;
                    this.reconnectActiveConferenceSession();
                }
            } catch (error) {
                this.logger.error("[WebConferenceService] onConnectionStateChangeEvent error : " + error);
            }
        });

        this.eventServiceSubscriptions.push(this.eventService.subscribe("ON_AUDIO_STREAM_TRACK_ENDED", async () => {
            setTimeout(() => {
                this.logger.info(`[WebConferenceService] ON_AUDIO_STREAM_TRACK_ENDED`);
                this.onAudioInputDeviceChange();
            }, 3000);
        }));

        this.eventServiceSubscriptions.push(this.eventService.subscribe("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD", async (__event, data) => {
            //we've taken a PBX call while having an active conference, we need to put the conference on hold
            this.putActiveConferenceOnHold(data?.call);
        }));

        if (this.virtualBackgroundService) {
            this.subscriptions.push(this.virtualBackgroundService.subscribe((event: RBEvent) => {
                if (event.name === VirtualBackgroundServiceEvents.UPDATE_STREAM) { this.onReplaceBackgroundChangeEvent(event); }
            }));
        }

        this.subscriptions.push(this.webrtcConfigAndUtils.subscribe((event: RBEvent) => { this.webrtcConfigAndUtilsEvent(event); }));

        this.subscriptions.push(this.deviceManagementService.subscribe((event: RBEvent) => {
            try {
                if (event.name === DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED) {
                    if (event.data.kind === "audioinput") { this.onAudioInputDeviceChange(); }
                    else if (event.data.kind === "videoinput") { this.onVideoInputDeviceChange(); }
                }
            }
            catch {
                //do nothing
            }
        }));

        this.subscriptions.push(this.bubbleConferenceRecapService.subscribe((event: RBEvent<BubbleConferenceRecapServiceEvents>) => {
            const recap: BubbleConferenceRecap = event.data as BubbleConferenceRecap;
            if (recap?.bubbleId) {
                switch (event.name) {
                    case BubbleConferenceRecapServiceEvents.ON_RECAP_CREATED:
                        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_CREATED, { roomDbId: recap.bubbleId, recap });
                        break;
                    case BubbleConferenceRecapServiceEvents.ON_RECAP_READY:
                        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_READY, { roomDbId: recap.bubbleId, recap });
                        break;
                    case BubbleConferenceRecapServiceEvents.ON_RECAP_REMOVED:
                        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_REMOVED, { roomDbId: recap.bubbleId, recap });
                        break;
                    case BubbleConferenceRecapServiceEvents.ON_RECAP_SHARED:
                        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_RECAP_SHARED, { roomDbId: recap.bubbleId, recap });
                        break;
                    default: break;
                }
            }
        }));
    }

    /**
     * Get the web conference session by it's ID
     * @param id - The ID of the web conference session (i.e. also equal to the DB ID of the room)
     * @returns Returns the WebConferenceSession (or null otherwise)
     */
    public getWebConferenceSessionById(id: string = null): WebConferenceSession | undefined {
        return this.webConferenceSessions[id];
    }

    /**
     * Returns true if the user is currenty participating in any web conference
     * @returns Returns true if the user is currenty participating in any web conference
     */
    public hasActiveWebConferenceSession(): boolean {
        let result = false;
        if (this.webConferenceSessions) {
            Object.keys(this.webConferenceSessions).forEach((key) => {
                if (this.webConferenceSessions[key]?.haveJoined) {
                    result = true;
                }
            });
        }
        return result;
    }

    /**
     * Returns WebConferenceSession if the user has an active web conference session (that he has joined)
     * @returns Returns WebConferenceSession or null instead
     */
    public getActiveWebConferenceSession(): WebConferenceSession | null {
        let webConferenceSession = null;
        if (this.webConferenceSessions) {
            Object.keys(this.webConferenceSessions).forEach((key) => {
                if (this.webConferenceSessions[key]?.haveJoined) {
                    webConferenceSession = this.webConferenceSessions[key];
                }
            });
        }
        return webConferenceSession;
    }

    public async getUnjoinedWebConferenceSessionByOwnerName(name: string): Promise<WebConferenceSession | null> {
        this.logger.info(`[WebConferenceService] getUnjoinedWebConferenceSessionByOwnerName ${name}`);
        if (this.webConferenceSessions) {
            const promises = Object.keys(this.webConferenceSessions).map(async (key) => {
                const webConferenceSession = this.webConferenceSessions[key];
                if (webConferenceSession?.status === BubbleConferenceStatus.UNJOINED) {
                    // The conference owner is the user that has started the conference
                    // Check if the bubble conference owner's displayname is the searched name
                    const webConferenceSessionOwnerId = webConferenceSession.bubble.getBubbleConferenceOwnerId();
                    if (webConferenceSessionOwnerId) {
                        const webConferenceSessionOwner: Contact = await this.contactService.getContactByDBId(webConferenceSessionOwnerId);
                        this.logger.info(`[WebConferenceService] getUnjoinedWebConferenceSessionByOwnerName searched name ${name} sessions iteration : owner ${webConferenceSessionOwner?.nameForLogs}`);
                        if (webConferenceSessionOwner?.displayName === name) {
                            this.logger.info(`[WebConferenceService] getUnjoinedWebConferenceSessionByOwnerName found sessionOwner ${webConferenceSessionOwner.nameForLogs} for searched name ${name}`);
                            return webConferenceSession;
                        }
                    }
                }
                return null;
            });

            const promisesResults = await Promise.allSettled(promises);

            const foundFirstSessionNonNull = promisesResults.find(
                (result): result is PromiseFulfilledResult<WebConferenceSession | null> =>
                    result.status === 'fulfilled'
                    && result.value !== null
            )?.value;

            this.logger.info(`[WebConferenceService] getUnjoinedWebConferenceSessionByOwnerName found session ${foundFirstSessionNonNull?.id} for searched name ${name}`);

            return foundFirstSessionNonNull;
        }
        return null;
    }

    public putActiveConferenceOnHold(call?) {
        const webConference = this.getActiveWebConferenceSession();
        if (!webConference || webConference?.onHold) return;

        if (webConference.status === BubbleConferenceStatus.CONNECTING) return; //CRRAINB-40017

        //check if the call that is accepted is the phone call related to the bubble, then do not put on hold
        if (call?.webConferenceRoom?.id === webConference.id) {
            this.logger.debug(`[WebConferenceService] do not putActiveConferenceOnHold ${webConference.id} because PSTN call is related`);
            return;
        }

        this.logger.debug(`[WebConferenceService] putActiveConferenceOnHold ${webConference.id}`);
        webConference.onHold = true;
        webConference.disableAutoSubscriptions = true;
        webConference.status = BubbleConferenceStatus.ON_HOLD;

        //remove all current videos
        this.removeAllRemoteVideoSessions(webConference);
        this.webrtcConfigAndUtils.RTC.clearMediaStream([document.getElementById("globalAudioTag")]);

        if (webConference.localParticipant?.videoSession) {
            this.removeMediaFromWebConference(webConference, BubbleConferenceMedia.VIDEO);
        }

        if (webConference.audioSession && webConference.localStreams?.length) {
            //do a local mute and put the conference to no audio state
            webConference.getAudioSession()?.muteAudio(true);
            webConference.noAudio = true;
            if (!webConference.localParticipant.noMicrophone) {
                this.updateParticipant(webConference, webConference.localParticipant.id, "unplug");
            }
        }
        // webConference.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: BubbleConferenceStatus.ON_HOLD });
    }

    public retrieveConferenceOnHold() {
        const webConference = this.getActiveWebConferenceSession();
        if (!webConference || !webConference?.onHold) return;

        this.eventService.publish("PUT_ACTIVE_TELEPHONY_CALL_ON_HOLD", { conferenceSession: webConference });

        webConference.onHold = false;
        webConference.disableAutoSubscriptions = false;
        webConference.status = BubbleConferenceStatus.CONNECTED;

        if (webConference.audioSession && webConference.localStreams?.length) {
            webConference.noAudio = false;
            //do a local mute and put the conference to no audio state
            webConference.getAudioSession()?.muteAudio(false);

            if (webConference.localParticipant.noMicrophone) {
                this.updateParticipant(webConference, webConference.localParticipant.id, "plug");
            }
        }

        //remove all current videos
        this.attachRemoteAudioStream(webConference);
        // webConference.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: BubbleConferenceStatus.CONNECTED });
        this.sendCustomEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD, { roomDbId: webConference.id });
    }

    /**
     * Joins a web conference in no audio mode. Only video & sharing sessions are available
     * @param bubble - The bubble where the user wants to join a conference (there should be an active conference in the bubble)
     * @param audioOnDeskphone - If true, the user has already joined with audio on the deskphone, so dont set the noAudio flag
     * @returns Returns WebConferenceSession object that is related to the web conference session in that bubble
     */
    private async joinWebConferenceWithoutAudio(bubble: BubbleRB, audioOnDeskphone: boolean = false): Promise<WebConferenceSession> {
        try {
            if (this.makingCall) {
                this.logger.warn("[WebConferenceService] joinWebConferenceWithoutAudio -- already joining a conference");
                return this.webConferenceSessions[bubble.dbId];
            }
            if (!bubble) { throw Error("Wrong or missing parameters"); }

            this.logger.debug("[WebConferenceService] joinWebConferenceWithoutAudio -- " + bubble.dbId);

            this.makingCall = true;

            this.contactService.setBusyState("dnd", "audio");
            let webConference = this.webConferenceSessions[bubble.dbId];
            if (!webConference) {
                webConference = new WebConferenceSession(bubble.dbId, bubble);
                this.webConferenceSessions[bubble.dbId] = webConference;
            }

            if (!webConference.localParticipant) {
                webConference.localParticipant = BubbleConferenceParticipant.create(this.contactService.userContact.dbId);
                webConference.localParticipant.contact = this.contactService.userContact;
            }

            if (webConference.status !== BubbleConferenceStatus.CONNECTING) {
                webConference.status = BubbleConferenceStatus.CONNECTING;
                // webConference.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: BubbleConferenceStatus.CONNECTING });
            }

            const resourceId = this.xmppService.getResourceFromJid(this.xmppService.fullJid);
            const params: any = { mute: false, media: ["video"], resourceId };

            if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.UNMUTE_REQUEST)) {
                params["askToUnmute"] = true;
            }

            if (this.webrtcConfigAndUtils?.webrtcConfig?.dc) {
                params.dc = this.webrtcConfigAndUtils.webrtcConfig.dc;
            }

            //in case we should pop-out the conf, send event here after we've taken the media from the conf
            if (this.settingsService.getBooleanSetting("openPopoutOnAnswerCall")) {
                this.sendInnerEvent('CONFERENCE_AUTO_POPOUT', { roomDbId: bubble.dbId });
            }

            webConference.localStreams = [];

            if (!audioOnDeskphone) webConference.noAudio = true;
            else webConference.audioOnDeskphone = true;

            webConference.haveJoined = true;

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/join`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] joinWebConferenceWithoutAudio -- successfully");
            this.makingCall = false;
            const jingleJid = data.jingleJid;
            webConference.jingleJid = jingleJid;

            //check if the conference owner is me, then this is case of switch device
            const confOnwerId = bubble.getBubbleConferenceOwnerId();
            if (!webConference.isMyConference && confOnwerId && confOnwerId === this.contactService.userContact.dbId) {
                webConference.isMyConference = true;
            }

            //in case of no audio, the join webconference event should be send right away
            webConference.status = BubbleConferenceStatus.CONNECTED;
            webConference.startDuration();
            // webConference.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: BubbleConferenceStatus.CONNECTED });
            this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_CONFERENCE_UPDATE_SUPERVISION_STATE);

            return webConference;
        }
        catch (error) {
            this.makingCall = false;
            this.webConferenceSessions[bubble.dbId].haveJoined = false;
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'joinWebConferenceWithoutAudio');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);

            this.removeActiveConferenceSession(this.webConferenceSessions[bubble.dbId]);

            this.resetBusyStateIfNeeded();

            const data = {
                popupTitle: "unableToJoin",
                popupBody: "joinMeetingFailure",
                okLabel: "ok"
            };

            if (errorInfo?.error) {
                if (errorInfo.error.errorDetailsCode === 403615) { data.popupBody = "maximumNumberConferenceParticipantsReach"; }
                else if (errorInfo.error.errorDetailsCode === 403620) { data.popupBody = "maximumNumberConferenceParticipantsReach"; }
                else if (errorInfo.error.errorDetailsCode === 403060) { data.popupBody = "lockedConferenceError"; }
                else if (errorInfo.error.errorDetailsCode === 403082) { data.popupBody = "switchDeviceConfError"; }
            }

            this.mainService.showGlobalPopupMessage(data);
            this.webConferenceSessions[bubble.dbId].status = BubbleConferenceStatus.ENDED;
            // this.webConferenceSessions[bubble.dbId].sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.ENDED });
            delete this.webConferenceSessions[bubble.dbId];

            if (errorInfo?.error && errorInfo.error.errorDetailsCode === 403068) {
                //the conference no longer exists for this room, clear all data
                if (bubble.isBubbleConferenceStarted()) {
                    bubble.conference.sessions = [];
                }
                bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);
            }

            throw errorInfo.error;
        }
    }

    private manageCodecs() {
        const codecs = {};
        let storedVideoCodec = 'vp8';
        let storedSharingCodec = 'vp8';
        if (this.webrtcConfigAndUtils?.companyConfig?.videoExt?.preferredCodec === "h264") { storedVideoCodec = 'h264'; }
        if (localStorage.getItem('webinarVideoCodec')) { storedVideoCodec = localStorage.getItem('webinarVideoCodec'); }

        if (this.webrtcConfigAndUtils?.companyConfig?.sharingExt?.preferredCodec === "h264") { storedSharingCodec = 'h264'; }
        if (localStorage.getItem('webinarSharingCodec')) { storedSharingCodec = localStorage.getItem('webinarSharingCodec'); }
        if (storedVideoCodec) codecs['video'] = storedVideoCodec;
        if (storedSharingCodec) codecs['sharing'] = storedSharingCodec;
        return codecs;
    }

    /**
     * API that tells if the user can use the addPSTNParticipantToConference with external phone numbers, or only to call rainbow users
     * @returns Returns TRUE if user can add participantPhoneNumbers to the conference
     */
    public canAddPSTNParticipantToWebConference(): boolean {
        const voiceSubscriberUserContact: boolean = this.contactService.userContact.isRainbowHubUser();
        const hybridDialOutCapability: boolean = this.contactService.userContact.isHybridDialOutCapability();
        const hasNumber: boolean = Boolean(this.contactService.userContact.phoneInternalNumber || this.contactService.userContact.phonePbx);

        this.logger.debug(`[WebConferenceService] - canAddPSTNParticipantToWebConference voiceUser '${voiceSubscriberUserContact}' hybridUserDialOutCapability '${hybridDialOutCapability}' hasNumber ${hasNumber}`);
        return (voiceSubscriberUserContact || hybridDialOutCapability) && hasNumber && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_ADD_PARTICIPANT);
    }

    /**
     * Add (call) rainbow users or PSTN participants in the conference
     * @param webConferenceSession - The web conference where we want to add the participants
     * @param participantPhoneNumbers - The phone numbers to be added to the conference
     * @param userIds - the rainbow user Ids to be added to the conference
     */
    public async addPSTNParticipantToWebConference(webConferenceSession: WebConferenceSession, participantPhoneNumbers: string[], userIds: string[]) {
        try {
            if (!webConferenceSession || !(participantPhoneNumbers?.length || userIds?.length)) {
                throw new Error("[WebConferenceService] addPSTNParticipantToWebConference -- missing session or parameters");
            }

            //RQ 7812 add PSTN participant dependant on TELEPHONY_ADD_PARTICIPANT flag
            if (participantPhoneNumbers?.length && !this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_ADD_PARTICIPANT)) {
                throw new Error("[WebConferenceService] addPSTNParticipantToWebConference -- user does not have the right feature to use this function");
            }

            this.logger.debug("[WebConferenceService] addPSTNParticipantToWebConference for wb conference: " + webConferenceSession.id + " - phoneNumbers: " + participantPhoneNumbers?.join(',') + " - userIds: " + userIds?.join(','));

            const participants = []; //RQRAINB-8332
            if (participantPhoneNumbers?.length) {
                participantPhoneNumbers.forEach(elt => participants.push({ phoneNumber: elt }));
            }
            if (userIds?.length) {
                userIds.forEach(elt => participants.push({ userId: elt }));
            }
            const params = { participants };
            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/add`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw response; }
            this.logger.info("[ConferenceService] addPSTNParticipantToWebConference success");
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addPSTNParticipantToWebConference');
            this.logger.error(`[ConferenceService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Decline incoming invitation to join a web conference. This API will send update to all other connected devices to the user to notify;
     * @param bubble - The bubble to decline the web conference invitation
     * @returns Returns on API call success
     */
    public async declineInvitationToJoinConference(bubble: Bubble): Promise<void> {
        try {
            this.logger.debug("[WebConferenceService] declineInvitationToJoinConference");
            if (!bubble) { throw Error("Wrong or missing parameters"); }

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${bubble.dbId}/reject`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] declineInvitationToJoinConference -- successfully");
        }
        catch (error) {
            this.makingCall = false;
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'declineInvitationToJoinConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Add audio to an existing web conference session (ONLY IN CASE THE JOIN IS DONE VIA NOAUDIO OPTION)
     * @param webConferenceSession - the WebConferenceSession object where we want to add the media
     */
    public async addAudioToWebConferenceSession(webConferenceSession: WebConferenceSession) {
        if (webConferenceSession?.localParticipant && webConferenceSession.haveJoined && webConferenceSession.noAudio) {
            this.logger.info(`[WebConferenceService] addAudioToWebConferenceSession`);

            const mediaToGet = ["audio"];
            try {
                let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

                if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.speechEnhancementService.startProcessStream(webConferenceSession.id, stream, "[WebConferenceService] addAudioToWebConferenceSession");
                }

                webConferenceSession.localStreams.push(stream);

                if (webConferenceSession.audioSession) {
                    await webConferenceSession.audioSession.updateSessionStreamsAndTransceivers(webConferenceSession.localStreams);
                    webConferenceSession.audioSession.sendTransportReplace();

                    if (webConferenceSession.localParticipant.noMicrophone) {
                        webConferenceSession.noAudio = false;
                        this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "plug");
                    }
                }
                else {
                    //user is in no audio case
                    await this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "addAudio");
                    const callId = "web_" + crypto.randomUUID();
                    await this.initJingleCall(webConferenceSession.jingleJid, webConferenceSession.id, callId);
                    webConferenceSession.noAudio = false;
                }
            }
            catch {
                this.logger.info(`[WebConferenceService] onAudioInputDeviceChange -- audio device unplugged`);
                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.audioSession);
                webConferenceSession.audioSession.localStreams = [];
                await webConferenceSession.audioSession.updateSessionStreamsAndTransceivers([]);
                webConferenceSession.audioSession.sendTransportReplace();

                if (!webConferenceSession.localParticipant.noMicrophone) {
                    this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "unplug");
                }
            }
        }
    }

    private async applyConstraintsToSharingStream(session) {
        try {
            let frameRate;
            const maxBitRate = 1500000;
            const scaleResolutionDownBy = 1;
            const stream = session.localStreams[0];
            const senderTranceiver = session.localTransceivers[0].sender;
            //check and apply contraints for sharing session
            if (stream.getAudioTracks().length) {
                frameRate = 24;
            }

            if (stream && senderTranceiver) {
                const track = stream.getVideoTracks()[0];
                // Apply scale factor and maxBitRate
                const params = senderTranceiver.getParameters();
                params.encodings[0].scaleResolutionDownBy = scaleResolutionDownBy;
                params.encodings[0].maxBitrate = maxBitRate;
                senderTranceiver.setParameters(params);

                // Apply frame rate constraints on src track
                const constraints = track.getConstraints();
                if (frameRate) constraints.frameRate = frameRate;
                await track.applyConstraints(constraints);

                this.logger.info(`[WebConferenceService] applyConstraintsToSharingStream(${scaleResolutionDownBy}, frameRate:${frameRate}fps, maxBitRate: ${maxBitRate})`);
            }
        }
        catch (error) { this.logger.error(`[WebConferenceService] applyConstraintsToSharingStream -- failure -- ${error.message}`); }
    }

    /**
     * Removes a PSTN user from the web conference in the given bubble; This API can be used by
     * the moderators of the bubble to remove other participants too
     * @param webConferenceSession - The current web conference session
     * @param participantId - The participant ID that should be removed from the conference
     */
    private async disconnectPstnParticipantFromWebConference(webConferenceSession: WebConferenceSession, participantId: string): Promise<void> {
        try {
            this.logger.debug("[WebConferenceService] disconnectPstnParticipantFromWebConference");
            if (!webConferenceSession || !participantId) { throw Error("Wrong or missing parameters"); }

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/phone-numbers/${participantId}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] disconnectPstnParticipantFromWebConference -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'disconnectPstnParticipantFromWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private async innerDisconnectUserFromWebConference(webConferenceSession: WebConferenceSession, participantId: string): Promise<void> {
        try {
            //manage the case of PSTN external participant
            let participant = webConferenceSession.getParticipantById(participantId);

            if (!participant) {
                //try to find the participant by contact DB ID (in case of PSTN resolution)
                participant = webConferenceSession.findParticipantByContactDbId(participantId);
            }

            if (participant?.isPstnParticipant) {
                await this.disconnectPstnParticipantFromWebConference(webConferenceSession, participant.id);
                return;
            }

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${participantId}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] disconnectUserFromWebConference -- successfully");
            // WARNING: In SDK case not remove the webConference object, only when the conference is terminated
            if (participantId === this.contactService.userContact.dbId && this.webConferenceSessions[webConferenceSession.id]) {
                this.removeActiveConferenceSession(this.webConferenceSessions[webConferenceSession.id]);
            }
        }
        catch (error) {
            if (participantId === this.contactService.userContact.dbId && this.webConferenceSessions[webConferenceSession.id]) {
                this.removeActiveConferenceSession(this.webConferenceSessions[webConferenceSession.id]);
            }
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'disconnectUserFromWebConference');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Removes a user from the web conference in the given bubble; This API can be used by
     * the moderators of the bubble to remove other participants too
     * @param webConferenceSession - The current web conference session
     * @param participantId - The participant ID that should be removed from the conference
     */
    public async disconnectUserFromWebConference(webConferenceSession: WebConferenceSession, participantId: string): Promise<void> {
        if (!webConferenceSession || !participantId) { throw Error("[WebConferenceService] disconnectUserFromWebConference -- Wrong or missing parameters"); }

        this.logger.debug(`[WebConferenceService] disconnectUserFromWebConference ${webConferenceSession.id} -- ${participantId}`);

        if (!this.promiseInProgress[webConferenceSession.id]) {
            this.promiseInProgress[webConferenceSession.id] = new Promise<void>((resolve, reject) => {
                this.innerDisconnectUserFromWebConference(webConferenceSession, participantId)
                    .then(() => {
                        resolve();
                        if (participantId === this.contactService.userContact.dbId) {
                            webConferenceSession.status = BubbleConferenceStatus.UNJOINED;
                            // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                        }
                        delete this.promiseInProgress[webConferenceSession.id];
                    })
                    .catch((error) => {
                        reject(error);
                        delete this.promiseInProgress[webConferenceSession.id];
                    });
            });
        }
        return this.promiseInProgress[webConferenceSession.id];
    }

    /**
     * API used to subscribe to the dynamic feed (if not done already)
     * @param webConferenceSession - The Web Conference session
     */
    public async subscribeToDynamicFeed(webConferenceSession: WebConferenceSession) {
        try {

            if (webConferenceSession.dynamicFeedSession) {
                this.logger.warn(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id} -- already subscribed`);
                return;
            }

            if (webConferenceSession.dynamicFeedSubscriptionInProgress) {
                this.logger.warn(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id} -- subscription in progress`);
                return;
            }

            this.logger.info(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id}`);
            //find a participant with a video
            const publisher = webConferenceSession.participants.find((participant) => {
                return participant.isVideoAvailable;
            });

            if (!publisher) {
                this.logger.warn(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id} -- dynamic feed is not available`);
                return;
            }

            webConferenceSession.dynamicFeedSubscriptionInProgress = true;
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${publisher.id}/subscribe`;
            const headers = this.authService.getPostHeader();

            const params = {
                media: "video",
                dynamicFeed: true,
                subStreamLevel: "0"
            };

            const body = JSON.stringify(params);
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            webConferenceSession.dynamicFeedSubscriptionInProgress = false;
            this.logger.info(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id} -- success`);
        }
        catch (error) {
            this.logger.error(`[WebConferenceService] subscribeToDynamicFeed -- ${webConferenceSession.id} -- error`);
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'subscribeToParticipantVideo');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            webConferenceSession.dynamicFeedSubscriptionInProgress = false;

            throw errorInfo.error;
        }
    }

    /**
     * Subscribe to a conference service (like the grid 49 / mozaic)
     * @param webConferenceSession - The Web Conference session
     * @param serviceId - The serviceId to who we want to subscribe
     */
    public async subscribeToService(webConferenceSession: WebConferenceSession, serviceId: string) {
        try {
            webConferenceSession.subscribedToGridService = true;
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/services/${serviceId}/subscribe`;
            const headers = this.authService.getPostHeader();

            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const sid = (await response.json()).data.sid;
            this.logger.info(`[WebConferenceService] subscribeToService -- ${webConferenceSession.id} -- ${serviceId} -- ${sid} -- success `);
            webConferenceSession.gridServiceSubscription = {
                id: serviceId,
                sessionId: sid,
                videoSession: this.xmppService.connection.jingle.sessions[sid]
            };

            //if the webrtc session is already established, send the event on reception
            if (webConferenceSession.gridServiceSubscription.videoSession?.peerconnection) {
                if (webConferenceSession.gridServiceSubscription.videoSession.peerconnection.iceConnectionState === "connected") {
                    this.logger.info(`[WebConferenceService] video sessionEstablished -- grid/mozaic service -- ${webConferenceSession.id}`);
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                        mediaType: BubbleConferenceMedia.MOZAIC,
                        action: BubbleConferenceMediaAction.ADDED
                    });
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.MOZAIC, action: BubbleConferenceMediaAction.ADDED });
                }
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'subscribeToService');
            this.logger.error(errorInfo.message);
            webConferenceSession.subscribedToGridService = false;
            webConferenceSession.gridServiceSubscription = null;

            throw errorInfo.error;
        }
    }

    /**
     * Subscribe to an echo test service
     * @param webConferenceSession - The Web Conference session
     * @param serviceId - The serviceId to who we want to subscribe
     */
    public async subscribeToEchoTestService(webConferenceSession: WebConferenceSession, serviceId: string) {
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.bubble.dbId}/services/${serviceId}/subscribe`;
            const headers = this.authService.getPostHeader();

            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            const sid = (await response.json()).data.sid;
            this.logger.info(`[WebConferenceService] subscribeToEchoTestService -- ${webConferenceSession.id} -- ${webConferenceSession.bubble.dbId} -- ${serviceId} -- ${sid} -- success `);

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'subscribeToEchoTestService');
            this.logger.error(errorInfo.message);

            throw errorInfo.error;
        }
    }

    /**
     * Subscribe to a video of a participant
     * @param webConferenceSession - The Web Conference session
     * @param participant - The WebConferenceParticipant to who we want to subscribe
     * @param media - The type of media you want to subscribe to: video or sharing
     */
    public async subscribeToParticipantVideo(webConferenceSession: WebConferenceSession, participant: BubbleConferenceParticipant, media: string = "video") {
        try {
            this.logger.info(`[WebConferenceService] subscribeToParticipantVideo -- ${webConferenceSession.id} -- ${participant.id}`);
            participant.subscriptionInProgress = true;
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${participant.id}/subscribe`;
            const headers = this.authService.getPostHeader();

            const params: any = {
                media: media
            };

            if (media === "video" && participant.simulcast) {
                params.subStreamLevel = "0";
                participant.subStreamLevel = 0;
                participant.temporalLevel = 1;
                params.resourceId = this.xmppService.getResourceFromJid(this.xmppService.fullJid);
            }
            const body = JSON.stringify(params);
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            participant.subscriptionInProgress = false;
            this.logger.info(`[WebConferenceService] subscribeToParticipantVideo -- ${webConferenceSession.id} -- ${participant.id} -- success`);
        }
        catch (error) {
            this.logger.error(`[WebConferenceService] subscribeToParticipantVideo -- ${webConferenceSession.id} -- ${participant.id} -- error`);
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'subscribeToParticipantVideo');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            if (errorInfo.error && errorInfo.error.errorDetailsCode === 404032) {
                //participant does not have video anymore
                participant.isVideoAvailable = false;
            }
            participant.subscriptionInProgress = false;

            throw errorInfo.error;
        }
    }

    /**
     * Method updatePstnParticipant
     * @param webConferenceSession - The Web Conference session
     * @param participantId - ID of particpant to update
     * @param option - update data (could be on of: mute, unmute);
     */
    private async updatePstnParticipant(webConferenceSession: WebConferenceSession, participantId: string, option: string): Promise<any> {
        try {
            this.logger.info("[WebConferenceService] updatePstnParticipant( webConfId=" + webConferenceSession.id + "participantId=" + participantId + " option=" + option + ")");

            const params = { option: option };

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/phone-numbers/${participantId}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            // const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] updatePstnParticipant -- successfully ( webConfId=" + webConferenceSession.id + "participantId=" + participantId + " option=" + option + ")");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updatePstnParticipant');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * API used to update the conference parameters (can be used by the conference moderator)
     * @param webConferenceSession - The Web Conference session
     * @param option - could be one of:
     * - mute  (Mutes all participants, except requester.)
     * - unmute (Unmutes all participants)
     * - lock (Disables any future participant from joining conference)
     * - unlock (Unlocks the conference)
     */
    public async updateConferenceParameters(webConferenceSession: WebConferenceSession, option: string): Promise<void> {
        try {
            if (!webConferenceSession || !option) {
                throw new Error('[WebConferenceService] updateConferenceParameters - missing a parameter');
            }

            this.logger.info("[WebConferenceService] updateConferenceParameters( webConfId= " + webConferenceSession.id + ", option= " + option + ")");
            const params = { option: option };

            const body = JSON.stringify(params);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/update`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
            this.logger.info("[WebConferenceService] updateConferenceParameters( webConfId= " + webConferenceSession.id + ", option= " + option + ") -- successfully");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateConferenceParameters');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to delegate the conference to another participant
     * @param webConferenceSession - The Web conference session
     * @param participantId - the ID of the participant to whom we will delegate the conference
     */
    public async delegateConferenceToParticipant(webConferenceSession: WebConferenceSession, participantId: string): Promise<void> {
        try {
            this.logger.info("[WebConferenceService] delegateConferenceToParticipant for webConfId=" + webConferenceSession.id + "participantId=" + participantId);
            if (!webConferenceSession || !participantId) {
                throw new Error('[WebConferenceService] delegateConferenceToParticipant - missing a parameter');
            }

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${participantId}/delegate`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            // const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] delegateConferenceToParticipant -- successfully -- webConfId=" + webConferenceSession.id + "participantId=" + participantId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'delegateConferenceToParticipant');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to stop the echo test
     * @param roomDbId - the DB Id of the room where we want to get the stats
     */
    public async getConferenceTalkingTimeStats(roomDbId: string) {
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${roomDbId}/talking-time`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData = await response.json();
            this.logger.info(`[WebConferenceService] getConferenceTalkingTimeStats success -- ${responseData.data.participants.length}`);
            return responseData.data.participants;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getConferenceTalkingTimeStats');
            this.logger.error(`[WebConferenceService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to start the echo test
     */
    public async startMediaEchoTest() {
        this.logger.info("[WebConferenceService] startMediaEchoTest");
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/echotest`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) { throw response; }
            this.logger.info("[WebConferenceService] startMediaEchoTest success");
            const containerdata = (await response.json()).data;
            if (containerdata.jingleJid) {
                return containerdata.jingleJid;

            }
            throw (new Error("Something went wrong"));
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startMediaEchoTest');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to stop the echo test
     */
    public async stopMediaEchoTest() {
        this.logger.info("[WebConferenceService] stopMediaEchoTest");
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/stop-echotest`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) { throw response; }
            this.logger.info("[WebConferenceService] stopMediaEchoTest success");
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'stopMediaEchoTest');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to initiates a conference for pilot test.
     * https://git.openrainbow.org/cloud-services/webrtc-documentation/-/blob/main/chapters/pilot_test.md
     * @returns Identifyer of the bubble used for test
     */
    public async startMediaEchoTestV2(): Promise<string> {
        this.logger.info("[WebConferenceService] startMediaEchoTestV2");
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/start-pilot-test`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw response; }
            this.logger.info("[WebConferenceService] startMediaEchoTestV2 success");
            const containerdata = (await response.json()).data;
            if (containerdata.roomId) {
                return containerdata.roomId;

            }
            throw (new Error("Something went wrong"));
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'startMediaEchoTestV2');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Transfer P2P call to Conference
     * @param oldSession - Jingle session object related to the P2P session
     * @param jingleJid - The jid of the Janus server where we want to join
     * @param bubble - The bubble where the adhoc is done
     * @param myConference - Is this my conference (initiator of the conf adhoc)
     * @param shouldMute - Whether the user should mute after join in the conference
     */
    public async transferP2pCallToConference(oldSession: any, jingleJid: string, bubble: Bubble, myConference: boolean = false, shouldMute: boolean = false) {
        try {
            this.logger.info("[WebConferenceService] transferP2pCallToConference to " + bubble.dbId);

            //check if already in progress of transfer call
            if (this.webConferenceSessions[bubble.dbId]?.haveJoined) {
                this.logger.info("[WebConferenceService] transferP2pCallToConference to " + bubble.dbId + " -- already in progress or done");
                throw new Error("Already making transfer call");
            }

            this.makingCall = true;

            let makeSnapshot = false;

            this.contactService.setBusyState("dnd", "audio");

            let webConferenceSession = this.webConferenceSessions[bubble.dbId];
            if (!webConferenceSession) {
                makeSnapshot = true;
                webConferenceSession = new WebConferenceSession(bubble.dbId, bubble as BubbleRB);
                this.webConferenceSessions[bubble.dbId] = webConferenceSession;
            }

            webConferenceSession.bubble = bubble as BubbleRB;

            if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[bubble.dbId], status: BubbleConferenceStatus.CONNECTING });
            }

            //make snapshot only if I'm not the originator of the adhoc conference
            if (!myConference || makeSnapshot) {
                const confData = await this.makeSnapshotByRoomDbId(bubble.dbId);
                await this.updateWebConferenceFromSnapshotData(bubble.dbId, confData);
            }

            let stream = await this.getUserMediaForSession(bubble.dbId);

            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(bubble.dbId, stream, "[WebConferenceService] transferP2pCallToConference");
            }

            webConferenceSession.localStreams.push(stream);

            webConferenceSession.jingleJid = jingleJid;

            const callId = "web_" + crypto.randomUUID();
            await this.initJingleCall(jingleJid, bubble.dbId, callId, false);
            webConferenceSession.haveJoined = true;
            webConferenceSession.isMyConference = myConference;
            this.makingCall = false;

            try {
                if (oldSession && oldSession.localType.indexOf("sharing") !== -1) {
                    //in case of double sharing, take the sharing only for the owner of the conf
                    if (oldSession.remoteType.indexOf("sharing") === -1 || myConference) {
                        let index = oldSession.localStreams.length - 1;
                        //in case we've started with sharing and then added audio, get the right index
                        if (oldSession.localStreams[oldSession.localStreams.length - 1].getAudioTracks().length) {
                            index = 0;
                        }
                        const sharingStream = oldSession.localStreams[index].clone();
                        sharingStream["mediaStreamKind"] = oldSession.localStreams[index]["mediaStreamKind"];
                        //Delay load to avoid black screen
                        setTimeout(() => {
                            this.addMediaToWebConference(webConferenceSession, BubbleConferenceMedia.SHARING, sharingStream);
                        }, 1500);
                    }
                }

                if (oldSession && oldSession.localType.indexOf("video") !== -1) {
                    //Delay load to avoid black screen
                    setTimeout(() => {
                        this.addMediaToWebConference(webConferenceSession, BubbleConferenceMedia.VIDEO);
                    }, 1500);
                }

                if (shouldMute) {
                    setTimeout(() => {
                        this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "mute");
                    }, 1000);
                }
            } catch (err) {
                this.logger.error("[WebConferenceService] transferP2pCallToConference error on duplicate stream -- " + err);
                return;
            }
        } catch (error) {
            this.makingCall = false;
            this.logger.error("[WebConferenceService] transferP2pCallToConference error -- " + error);
        }
    }

    /**
     * Method used to put all current incoming video sessions on hold
     * @param webConferenceSession - The Web conference session
     */
    public putAllRemoteVideoSessionsOnHold(webConferenceSession: WebConferenceSession): void {
        this.logger.info("[WebConferenceService] putAllRemoteVideoSessionsOnHold for webConfId=" + webConferenceSession.id);
        if (!webConferenceSession) {
            throw new Error('[WebConferenceService] putAllRemoteVideoSessionsOnHold - missing a parameter');
        }

        webConferenceSession.participants.forEach((participant: BubbleConferenceParticipant) => {
            if (participant.videoSession && !participant.isSharingParticipant && !participant.videoSession.onHold) {
                participant.videoSession.sendHold(true);
            }
        });

        if (webConferenceSession.dynamicFeedSession && !webConferenceSession.dynamicFeedSession.onHold) {
            webConferenceSession.dynamicFeedSession.sendHold(true);
        }

        if (webConferenceSession.gridServiceSubscription?.videoSession && !webConferenceSession.gridServiceSubscription.videoSession.onHold) {
            webConferenceSession.gridServiceSubscription.videoSession.sendHold(true);
        }

        this.logger.info("[WebConferenceService] putAllRemoteVideoSessionsOnHold -- successfully -- webConfId=" + webConferenceSession.id);
    }

    /**
     * Method used to put the video session of the participant on hold; Cannot be done on sharing session !
     * @param participant - The Web conference participant
     */
    public async putRemoteVideoSessionOnHold(participant: BubbleConferenceParticipant) {
        if (!participant) { return; }
        this.logger.info("[WebConferenceService] putRemoteVideoSessionOnHold for webConfId=" + participant.id);
        if (participant?.videoSession && !participant.isSharingParticipant && !participant.videoSession.onHold) {
            await participant.videoSession.sendHold(true);
        }
    }

    /**
     * Method used to unhold the remote video session for participant; Cannot be done on sharing session !
     * @param participant - The Web conference participant
     */
    public async putRemoteVideoSessionOnUnhold(participant: BubbleConferenceParticipant) {
        if (!participant) { return; }
        this.logger.info("[WebConferenceService] putRemoteVideoSessionOnUnhold for webConfId=" + participant.id);
        if (participant?.videoSession && !participant.isSharingParticipant && participant.videoSession.onHold) {
            await participant.videoSession.sendHold(false);
        }
    }

    /**
     * Method used to remove all incoming video sessions; Also the dynamic feed / active talker video is removed
     * @param webConferenceSession - The Web conference participant
     */
    public removeAllRemoteVideoSessions(webConferenceSession: WebConferenceSession) {
        webConferenceSession.participants.forEach((participant: BubbleConferenceParticipant) => {
            if (participant.videoSession && !participant.isSharingParticipant) {
                this.xmppService.connection.jingle.terminate(participant.videoSession.sid);
                //update the stats for the session before removing
                this.updateStatisticsForSession(webConferenceSession, participant.videoSession.sid);
                delete this.webrtcConfigAndUtils.callsStats[participant.videoSession.sid];
                participant.videoSession = null;
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                    mediaType: BubbleConferenceMedia.VIDEO,
                    action: BubbleConferenceMediaAction.REMOVED,
                    participant: participant,
                });
            }
        });

        if (webConferenceSession.dynamicFeedSession) {
            this.xmppService.connection.jingle.terminate(webConferenceSession.dynamicFeedSession.sid);
            //update the stats for the session before removing
            this.updateStatisticsForSession(webConferenceSession, webConferenceSession.dynamicFeedSession.sid);
            delete this.webrtcConfigAndUtils.callsStats[webConferenceSession.dynamicFeedSession.sid];
            webConferenceSession.dynamicFeedSession = null;
            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                mediaType: BubbleConferenceMedia.DYNAMIC_FEED,
                action: BubbleConferenceMediaAction.REMOVED
            });
        }
        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.REMOVED });
    }

    /*
    *   PRIVATE FUNCTIONS
    */

    private async onVideoInputDeviceChange() {
        const webConferenceSession = this.getActiveWebConferenceSession();
        if (webConferenceSession?.localParticipant?.videoSession && webConferenceSession.haveJoined) {
            this.logger.info(`[WebConferenceService] onVideoInputDeviceChange`);

            setTimeout(async () => {
                const mediaToGet = ["video"];
                try {
                    if (webConferenceSession.originalVideoStream) {
                        webConferenceSession.originalVideoStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                        if (webConferenceSession.originalVideoStream.stop) { webConferenceSession.originalVideoStream.stop(); }
                        webConferenceSession.originalVideoStream = null;
                    }

                    this.webrtcConfigAndUtils.disableMediaStreams(webConferenceSession.localParticipant.videoSession.localStreams);
                    const stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

                    //change the audio track "on the fly" does not work, should renego the session
                    webConferenceSession.originalVideoStream = stream;

                    const modifiedStream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(stream) : stream;
                    try {
                        await webConferenceSession.localParticipant.videoSession.replaceTransceiverTrack(modifiedStream.getVideoTracks()[0]);
                        webConferenceSession.localParticipant.videoSession.localStreams = [];
                        webConferenceSession.localParticipant.videoSession.localStreams.push(modifiedStream);

                        this.logger.info(`[WebConferenceService] onVideoInputDeviceChange -- success 'on the fly'`);

                        this.sendEvent("peerConnectionEstablished", { sessionId: webConferenceSession.localParticipant.videoSession.sid });
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.ADDED });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.ADDED });
                    }
                    catch {
                        //change the audio track "on the fly" does not work, should renego the session
                        webConferenceSession.localParticipant.videoSession.localStreams = [];
                        webConferenceSession.localParticipant.videoSession.localStreams.push(modifiedStream);
                        await webConferenceSession.localParticipant.videoSession.updateSessionStreamsAndTransceivers(webConferenceSession.localParticipant.videoSession.localStreams);
                        webConferenceSession.audioSession.sendTransportReplace();

                        this.logger.info(`[WebConferenceService] onVideoInputDeviceChange -- success 'via transport-replace'`);

                        this.sendEvent("peerConnectionEstablished", { sessionId: webConferenceSession.localParticipant.videoSession.sid });
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.ADDED });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.ADDED });
                    }
                }
                catch {
                    this.logger.info(`[WebConferenceService] onVideoInputDeviceChange -- video device unplugged or not working -- remove media from conference and re-add it again`);
                    this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.VIDEO);
                    this.addMediaToWebConference(webConferenceSession, BubbleConferenceMedia.VIDEO);
                }
            }, 500);
        }
    }

    private async onAudioInputDeviceChange() {
        const webConferenceSession = this.getActiveWebConferenceSession();
        if (webConferenceSession?.localParticipant && webConferenceSession.haveJoined && !webConferenceSession.noAudio && !webConferenceSession.audioOnDeskphone && !this.audioProfileChanging) {
            this.logger.info(`[WebConferenceService] onAudioInputDeviceChange`);

            //send transport replace
            this.audioProfileChanging = true;
            setTimeout(async () => {
                this.audioProfileChanging = false;
                const mediaToGet = ["audio"];
                try {
                    this.webrtcConfigAndUtils.disableMediaStreams(webConferenceSession.localStreams);
                    webConferenceSession.localStreams = [];
                    let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

                    if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                        stream = await this.speechEnhancementService.startProcessStream(webConferenceSession.id, stream, "[WebConferenceService] onAudioInputDeviceChange");
                    }

                    try {
                        //try to change the audio track "on the fly"
                        await webConferenceSession.audioSession.replaceTransceiverTrack(stream.getAudioTracks()[0]);

                        webConferenceSession.localStreams.push(stream);

                        if (webConferenceSession.localParticipant.noMicrophone) {
                            this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "plug");
                        }

                        this.logger.info(`[WebConferenceService] onAudioInputDeviceChange -- success 'on the fly'`);
                    }
                    catch {
                        //change the audio track "on the fly" does not work, should renego the session
                        this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.audioSession);
                        webConferenceSession.localStreams = [];
                        webConferenceSession.localStreams.push(stream);
                        await webConferenceSession.audioSession.updateSessionStreamsAndTransceivers(webConferenceSession.localStreams);
                        webConferenceSession.audioSession.sendTransportReplace();

                        if (webConferenceSession.localParticipant.noMicrophone) {
                            this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "plug");
                        }

                        this.logger.info(`[WebConferenceService] onAudioInputDeviceChange -- success 'via transport-replace'`);
                    }
                }
                catch {
                    this.logger.info(`[WebConferenceService] onAudioInputDeviceChange -- audio device unplugged`);
                    this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.audioSession);
                    webConferenceSession.audioSession.localStreams = [];
                    await webConferenceSession.audioSession.updateSessionStreamsAndTransceivers([]);
                    webConferenceSession.audioSession.sendTransportReplace();

                    if (!webConferenceSession.localParticipant.noMicrophone) {
                        this.updateParticipant(webConferenceSession, webConferenceSession.localParticipant.id, "unplug");
                    }
                }
            }, 500);
        }
    }

    //Event that is fired when the background change is detected
    private async onReplaceBackgroundChangeEvent(event: RBEvent): Promise<void> {

        const webConferenceSession = this.getActiveWebConferenceSession();
        if (webConferenceSession?.localParticipant?.videoSession) {
            this.logger.info(`[WebConferenceService] onReplaceBackgroundChangeEvent`);
            const session = webConferenceSession.localParticipant.videoSession;
            let sessionStream = session.localStreams.pop();
            if (event.data.type === VIRTUAL_BACKGROUND_TYPE.NONE) {
                sessionStream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
                sessionStream = null;
            }
            const stream = event.data.stream;
            session.localStreams.push(stream);
            try {
                await session.replaceTransceiverTrack(stream.getVideoTracks()[0]);
                this.sendEvent("peerConnectionEstablished", { sessionId: session.sid });
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: "video", action: BubbleConferenceMediaAction.ADDED });
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: "video", action: BubbleConferenceMediaAction.ADDED });
            }
            catch {
                await session.updateSessionStreamsAndTransceivers(session.localStreams);
                session.sendTransportReplace();
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: "video", action: BubbleConferenceMediaAction.ADDED });
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: "video", action: BubbleConferenceMediaAction.ADDED });
            }
        }
    }

    /**
     * Method askToAddSharingToWebConferenceSession
     * Used only on desktop application : choose which screen to share
     */
    public askToAddSharingToWebConferenceSession(conversation, conferenceSession, shouldAskForAudio: boolean = false) {
        const proceed = !this.webrtcConfigAndUtils.askToAddSharing(conversation.id, shouldAskForAudio);

        //if TRUE, add sharing as we normally do; Else wait for centralizedService to call the function
        if (proceed) {
            this.addMediaToWebConference(conferenceSession, BubbleConferenceMedia.SHARING, undefined, shouldAskForAudio);
        }
    }

    /**
     * Method getUserMediaForSession
     * @param dbId - the ID of the web conference session
     */
    private async getUserMediaForSession(dbId: string): Promise<any> {
        const conference = this.webConferenceSessions[dbId];
        if (!conference) {
            throw Error("Web conference is no longer active");
        }

        try {
            const stream = await this.webrtcConfigAndUtils.getBrowserMedia(["audio"]);
            return stream;
        }
        catch (error) {
            this.logger.error("[WebConferenceService] getUserMediaForSession error -- getUserMedia failed -- " + error);
            this.sendEvent("joinWithoutAudio");
            return "noMic";
        }
    }

    private disableMediaStream(stream: MediaStream) {
        stream.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
        stream = null;
    }

    private async getSilentStream(): Promise<MediaStream> {
        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();
        const silentSource = audioContext.createOscillator();
        silentSource.frequency.setValueAtTime(0, audioContext.currentTime);
        silentSource.connect(destination);
        silentSource.start();
        return destination.stream;
    }

    /**
     * Method initCall
     * @param jid - the JID of the distant party to make call
     * @param dbId - the ID of the web conference session
     * @param callId - unique call ID for this session
     * @param forceTurn - a flag whether we should go only through TURN servers or not
     */
    private async initJingleCall(jid: string, dbId: string, callId: string, forceTurn: boolean = false) {
        try {
            await this.webrtcConfigAndUtils.setWebrtcConfiguration();
        }
        catch {
            this.logger.error("[WebConferenceService] initCall - setWebrtcConfiguration failed");
        }

        const session = await this.xmppService.connection.jingle.initiate(jid, this.xmppService.connection.jid, "audio", this.webConferenceSessions[dbId].localStreams, callId, dbId, null, forceTurn);
        this.webConferenceSessions[dbId].audioSession = session;

        //get statistics for the audio session
        this.createStatisticsForSession(dbId, session.sid);

        // this.statsIntervals.push(session.getStats(3000));
        this.statsIntervals.push(this.webrtcConfigAndUtils.getStatsForSession(session, 4000));

        // this.$rootScope.$broadcast("ON_WEB_CONFERENCE_UPDATE_EVENT", conference);
        // this.$rootScope.$broadcast("ON_CONFERENCE_UPDATED_EVENT", conference);
    }

    /**
     * Creates a new entry for metrics on the server related to this session
     * @param webConfId - the id of the conference
     * @param sessionId - the session id related to the conference
     */
    private async createStatisticsForSession(webConfId: string, sessionId: string) {
        this.logger.info("[WebConferenceService] createStatisticsForSession for conf " + webConfId + " and session " + sessionId);

        if (!webConfId || !sessionId) {
            this.logger.warn("[WebConferenceService] createStatisticsForSession -- missing parameters");
            return;
        }

        try {
            this.webrtcConfigAndUtils.callsStats[sessionId] = {};
            const metrics = [];
            metrics.push({ connectionId: sessionId, stats: this.webrtcConfigAndUtils.callsStats[sessionId] });
            const id = await this.webrtcConfigAndUtils.createStatisticsForSession(webConfId, webConfId, "pending");
            this.logger.info("[WebConferenceService] createStatisticsForSession success " + id);
            this.webrtcConfigAndUtils.callsStats[sessionId].id = id;
            this.webConferenceSessions[webConfId].metricsId = id;
            this.webConferenceSessions[webConfId].metricsState = "pending";
        }

        catch (error) {
            this.logger.error("[WebConferenceService] createStatisticsForSession error for conf " + webConfId);
        }
    }

    /**
     * Updates the metrics for the given session which is part of the conference
     * @param webConferenceSession - the WebConferenceSession
     * @param sessionId - the session id related to the conference
     */
    private updateStatisticsForSession(webConferenceSession: WebConferenceSession, sessionId: string) {
        if (!webConferenceSession || !sessionId) {
            this.logger.warn("[WebConferenceService] updateStatisticsForSession -- missing parameters");
            return;
        }

        if (!webConferenceSession?.metricsId) {
            this.logger.warn("[WebConferenceService] updateStatisticsForSession -- missing webConference or no metricsId");
            return;
        }

        if (!webConferenceSession.metricsState) {
            webConferenceSession.metricsState = "pending";
        }

        const metrics = [];

        //check if statistics already exist
        if (this.webrtcConfigAndUtils.callsStats[sessionId] && Object.keys(this.webrtcConfigAndUtils.callsStats[sessionId]).length) {
            if (this.webrtcConfigAndUtils.callsStats[sessionId].RainMetrics) { this.webrtcConfigAndUtils.callsStats[sessionId].push(this.webrtcConfigAndUtils.callsStats[sessionId].RainMetrics); }
            metrics.push({ connectionId: sessionId, stats: this.webrtcConfigAndUtils.callsStats[sessionId] });
            this.webrtcConfigAndUtils.updateStatisticsForCall(webConferenceSession.metricsId, webConferenceSession.id, webConferenceSession.metricsState, metrics);
        }
    }

    /**
     * Called when the conference is delegated to another user
     * @param webConfId - the webConfId
     * @param userId - the userId that is now the owner of the conference
     */
    public onConferenceDelegated(webConfId: string, userId: string) {
        const webConferenceSession = this.getWebConferenceSessionById(webConfId);
        if (!webConferenceSession || !userId) {
            this.logger.warn("[WebConferenceService] onConferenceDelegated -- missing parameters or no existing conference");
            return;
        }

        let gaveOwnership = false;
        if (userId === this.contactService.userContact.dbId && !webConferenceSession.isMyConference) {
            webConferenceSession.isMyConference = true;
        }
        else if (userId !== this.contactService.userContact.dbId && webConferenceSession.isMyConference) {
            webConferenceSession.isMyConference = false;
            gaveOwnership = true;
        }

        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_DELEGATE, { roomDbId: webConferenceSession.id, userDbId: userId, gaveOwnership });
    }

    /**
     * Make snapshot to room and get the conference session info related to that room
     * Data received from Server
     * data	Object
                active	Boolean  -- Is there an active conference?
                participants	Object[]
                    userId  optional	String
                    phoneNumber optional	String
                    participantRole optional	String (moderator, member, guest)
                total optional	Number
                limit   optional	Number	(Default value: 100)
                offset  optional	Number
     * @param roomDbId - The Web conference session
     */
    private async makeSnapshotByRoomDbId(roomDbId: string): Promise<any> {
        try {
            if (!roomDbId) {
                throw new Error('[WebConferenceService] makeSnapshotByRoomDbId - missing a parameter');
            }
            this.logger.info("[WebConferenceService] makeSnapshotByRoomDbId for roomDbId " + roomDbId);

            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${roomDbId}/snapshot`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            this.logger.info("[WebConferenceService] makeSnapshotByRoomDbId -- successfully -- roomDbId " + roomDbId);
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'makeSnapshotByRoomDbId');
            this.logger.error(`[WebConferenceService] -- ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private async createSessionParticipantFromSnapshotData(snapshotDataParticipant: any): Promise<BubbleConferenceParticipant | null> {
        //get participants and create them one by one
        let contactId = snapshotDataParticipant.userId;
        const phoneNumber = snapshotDataParticipant.phoneNumber;
        const associatedContactId = snapshotDataParticipant.associatedUserId;
        const associatedGroupName = snapshotDataParticipant.associatedGroupName;
        let contact: Contact;
        if (!contactId && associatedContactId) {
            contact = await this.contactService.getContactByDBId(associatedContactId);
            contactId = phoneNumber;
        }
        else if (!contactId) {
            contact = this.contactService.createBasicContact(null, phoneNumber);
            contactId = phoneNumber;
            if (associatedGroupName) {
                contact.displayName = associatedGroupName;
            }
        }
        else {
            contact = await this.contactService.getContactByDBId(contactId);
        }

        //do not add ringing guest PSTN participants
        if (snapshotDataParticipant?.participantRole === "guest" && snapshotDataParticipant?.participantState === "ringing") {
            return null;
        }

        if (contact) {
            const sessionParticipant = BubbleConferenceParticipant.create(contactId);
            sessionParticipant.contact = contact;
            sessionParticipant.mute = false;
            sessionParticipant.delegateCapability = (snapshotDataParticipant.participantRole === "moderator");
            sessionParticipant.role = snapshotDataParticipant.participantRole;
            sessionParticipant.noMicrophone = false;
            sessionParticipant.raiseHand = snapshotDataParticipant.raiseHand;
            sessionParticipant.raiseHandDate = snapshotDataParticipant.raiseHandDate;
            if (snapshotDataParticipant.publishers) {
                snapshotDataParticipant.publishers.forEach((pub) => {
                    if (pub.mediaType === "video") {
                        sessionParticipant.isVideoAvailable = true;
                    } else if (pub.mediaType === "sharing") {
                        sessionParticipant.isSharingParticipant = true;
                    }
                });
            }
            if (phoneNumber) {
                sessionParticipant.isPstnParticipant = true;
            }
            return sessionParticipant;
        }

        return null;
    }

    private async updateWebConferenceFromSnapshotData(roomDbId: string, confData: any) {
        if (!roomDbId || !this.webConferenceSessions[roomDbId] || !confData) {
            throw new Error('[WebConferenceService] updateWebConferenceFromSnapshotData - missing a parameter or conf session');
        }

        if (confData.active) {
            const participantsPromise = [];
            confData.participants.forEach((participant) => { participantsPromise.push(this.createSessionParticipantFromSnapshotData(participant)); });
            const participants: BubbleConferenceParticipant[] = await Promise.all(participantsPromise);
            participants.forEach((participant: BubbleConferenceParticipant) => {
                if (participant) {
                    if (this.contactService.isUserContact(participant.contact) && !this.webConferenceSessions[roomDbId].localParticipant) {
                        this.webConferenceSessions[roomDbId].localParticipant = participant;
                    }
                    else {
                        this.webConferenceSessions[roomDbId].addOrUpdateParticipant(participant, true);
                    }
                    this.logger.info(`[WebConferenceService] updateWebConferenceFromSnapshotData participant added -- ${participant.id} -- ${roomDbId}`);
                }
            });
        }
    }

    /*
    *   XMPP EVENTS AND HELPER FUNCTIONS
    */

    //remove all local audio / video / sharing streams for the session
    private disableAllLocalStreamsForSession(webConferenceSession: WebConferenceSession): void {
        try {
            this.webrtcConfigAndUtils.disableMediaStreams(webConferenceSession.localStreams);
            if (webConferenceSession.originalVideoStream) {
                this.webrtcConfigAndUtils.disableMediaStream(webConferenceSession.originalVideoStream);
            }
            if (webConferenceSession.audioSession) {
                this.updateStatisticsForSession(webConferenceSession, webConferenceSession.audioSession.sid);
                //update the stats for the session before removing
                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.audioSession);
                this.xmppService.connection.jingle.terminate(webConferenceSession.audioSession.sid);

                if (this.speechEnhancementService) this.speechEnhancementService.stopProcessStream(webConferenceSession.id, "[WebConferenceService] disableAllLocalStreamsForSession");
                delete this.webrtcConfigAndUtils.callsStats[webConferenceSession.audioSession.sid];
            }
            if (webConferenceSession.localParticipant?.videoSession) {
                this.updateStatisticsForSession(webConferenceSession, webConferenceSession.localParticipant.videoSession.sid);
                webConferenceSession.localParticipant.videoSession.terminate();
                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.localParticipant.videoSession);
                delete this.webrtcConfigAndUtils.callsStats[webConferenceSession.localParticipant.videoSession.sid];
            }
            if (webConferenceSession.localSharingSession) {
                this.updateStatisticsForSession(webConferenceSession, webConferenceSession.localSharingSession.sid);
                webConferenceSession.localSharingSession.terminate();
                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.localSharingSession);
                delete this.webrtcConfigAndUtils.callsStats[webConferenceSession.localSharingSession.sid];
            }
            if (webConferenceSession.gridServiceSubscription?.videoSession) {
                this.updateStatisticsForSession(webConferenceSession, webConferenceSession.gridServiceSubscription.videoSession.sid);
                webConferenceSession.gridServiceSubscription.videoSession.terminate();
                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.gridServiceSubscription.videoSession);
                webConferenceSession.gridServiceSubscription.videoSession = null;
                delete this.webrtcConfigAndUtils.callsStats[webConferenceSession.gridServiceSubscription.videoSession.sid];
            }
        }
        catch (error) {
            this.logger.warn(`[WebConferenceService] disableAllLocalStreamsForSession ${webConferenceSession?.id} error ${error}`);
        }
    }

    private removeActiveConferenceSession(webConference: WebConferenceSession, removeAlsoForSDK: boolean = false) {
        if (webConference) {

            //sdk specific case
            if (config.sdk && !removeAlsoForSDK) {
                webConference.status = BubbleConferenceStatus.UNJOINED;
            }
            //norma ucaas case
            else {
                webConference.status = BubbleConferenceStatus.ENDED;
            }

            webConference.metricsState = "ended";
            webConference.haveJoined = false;
            this.disableAllLocalStreamsForSession(webConference);
            webConference.audioSession = null;
            webConference.localParticipant = null;
            webConference.liveTranscriptionAvailable = false;
            webConference.liveTranscription = undefined;
            //webConferenceSession.sendEvent(WebConferenceEvents.ON_STATUS_UPDATE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: webConference.status });
            //this event is send for telephony service, so that we can release the phone call related to the bubble in case of PBX call in // with the bubble
            this.eventService.publish("ON_WEB_CONFERENCE_RELEASED", { roomDbId: webConference.id });

            //do not delete the web conference session in case of SDK, in the case the user has only left the conference;
            //if the conference is ended, this will remove it completely from the sessions object
            if (!config.sdk || removeAlsoForSDK) {
                delete this.webConferenceSessions[webConference.id];
                webConference = null;
            }

            this.resetBusyStateIfNeeded();

            //clear all stats intervals
            let statsInterval;
            while (this.statsIntervals.length) {
                statsInterval = this.statsIntervals.pop();
                if (statsInterval) {
                    window.clearInterval(statsInterval);
                    statsInterval = null;
                }
            }

            //send event for desktop app to stop highlight
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_STOP_HIGHLIGHTING);

            //open popup for guest to finalize account
            if (this.contactService.userContact.guestMode) {
                this.eventService.publish("ON_OPEN_POPUP", "concludeInvitation");
            }

            this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_CONFERENCE_UPDATE_SUPERVISION_STATE);
        }
    }

    private resetBusyStateIfNeeded(): void {
        this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
    }

    private async createSessionParticipantFromElem(participantElem: any, webConferenceSession: WebConferenceSession): Promise<BubbleConferenceParticipant> {
        try {
            let contactId = participantElem.find('user-id').text();
            const role = participantElem.find('role').text();
            const phoneNumber = participantElem.find('phone-number').text();
            const associatedContactId = participantElem.find('associated-user-id').text();
            const associatedGroupName = participantElem.find('associated-group-name').text();
            let contact: Contact;
            if (!contactId && associatedContactId) {
                contact = await this.contactService.getContactByDBId(associatedContactId);
                contactId = phoneNumber;
                this.logger.debug(`[WebConferenceService] createSessionParticipantFromElem - associated-user-id ${associatedContactId} found for phoneNumber ${phoneNumber}`);
            }
            else if (!contactId) {
                contact = this.contactService.createBasicContact(null, phoneNumber);
                contactId = phoneNumber;
                if (associatedGroupName) {
                    contact.displayName = associatedGroupName;
                    this.logger.debug(`[WebConferenceService] createSessionParticipantFromElem - associated-group-name ${associatedGroupName} found for phoneNumber ${phoneNumber}`);
                }
            }
            else {
                contact = await this.contactService.getContactByDBId(contactId);
            }
            let microphone = participantElem.find("microphone").text();
            if (!microphone || microphone === "true") { microphone = true; }
            else if (microphone === "false") { microphone = false; }

            //update talking time for participant
            const talkingTime = $(participantElem).find("talking-time").text();
            const talking = $(participantElem).find("talking").text() === "true";

            if (contact) {
                const sessionParticipant = BubbleConferenceParticipant.create(contactId);
                sessionParticipant.contact = contact;
                sessionParticipant.mute = participantElem.find('mute').text() === 'true';
                sessionParticipant.delegateCapability = participantElem.find("delegate-capability").text() === 'true';
                sessionParticipant.role = role;
                sessionParticipant.noMicrophone = !microphone;
                if (phoneNumber) {
                    sessionParticipant.isPstnParticipant = true;
                }

                const talkingTimeValue = talkingTime ? parseInt(talkingTime, 10) : 0;
                webConferenceSession.updateTalkingTimeForParticipant(contactId, talkingTimeValue);

                if (talking) {
                    webConferenceSession.talker = contactId;
                }

                const raiseHandInfo = this.raiseHandTemp?.find(hand => hand.userId === contactId);
                if (raiseHandInfo) {
                    sessionParticipant.raiseHand = true;
                    sessionParticipant.raiseHandDate = new Date(raiseHandInfo.date);
                    this.logger.infoEvent(`[WebConferenceService] onConferenceMessage -- Raising participant's hand ${contactId} (roomId: ${webConferenceSession.bubble.dbId})`);
                }

                return sessionParticipant;
            }

            return null;
        }
        catch (error) {
            this.logger.error(`[WebConferenceService] createSessionParticipantFromElem -- Failure`);
            return null;
        }
    }

    private onXmppJingleMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const fromJid = stanzaElem.attr("from");
            const bareJid = this.xmppService.getBareJidFromJid(fromJid);

            const proposeNode = stanzaElem.find("propose");
            const conferenceNode = proposeNode.find("conference");
            const conferenceSessionId = conferenceNode.attr("id");

            const conferenceSession = this.getActiveWebConferenceSession();

            if (conferenceSession && conferenceSession.id === conferenceSessionId) {
                // handle internally messages from janusgateway
                try {
                    if (stanzaElem.find("propose").length > 0) {
                        this.logger.info("[WebConferenceService]   | onProposeReceived -- auto accept it for this client");
                        const id = stanzaElem.find("propose").attr("id");

                        //send accept to my ressources
                        const xmppMessage = $msg({ from: this.contactService.userContact.fullJid, to: this.contactService.userContact.jid, id: "web_" + crypto.randomUUID() })
                            .c("accept", { xmlns: this.nameSpaceJingle, id: id });

                        this.xmppService.send(xmppMessage);

                        //send proceed
                        const xmppProceedMessage = $msg({ from: this.contactService.userContact.fullJid, to: bareJid, id: "web_" + crypto.randomUUID() })
                            .c("proceed", { xmlns: this.nameSpaceJingle, id: id });

                        this.xmppService.send(xmppProceedMessage);

                        return true;
                    }
                }
                catch (error) {
                    //do nothing
                    this.logger.error(`[WebConferenceService] onXmppJingleMessage -- ${error?.message}`);
                }
            }

            return true;
        }
        catch {
            return true;
        }
    }

    private async onConferenceMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const roomId = stanzaElem.find('room-id').text();
            let webConferenceSession: WebConferenceSession = this.webConferenceSessions[roomId];
            if (!webConferenceSession || webConferenceSession.status === 'unjoined' || webConferenceSession.status === 'ended') {
                this.logger.debug(`[WebConferenceService] onConferenceMessage -- roomId ${roomId} -- no active web conference session`);
                const conferenceInfoElems = stanzaElem.find('conference-info');

                let myParticipantFound = false;
                if (conferenceInfoElems.length) {
                    //handle conference action from other client
                    const action = conferenceInfoElems.attr('action');
                    if (action === "reject") {
                        this.logger.debug(`[WebConferenceService] onConferenceMessage -- roomId ${roomId} -- action reject received`);
                        this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_WEBCONFERENCE_HIDE_INVITATION, { roomDbId: roomId });
                        return true;
                    }

                    //check if user connected using another device
                    const participantElems = stanzaElem.find('participants').find('participant');
                    if (participantElems.length) {
                        participantElems.each((__i: number, participantElem: Element) => {
                            const contactId = $(participantElem).find('user-id').text();
                            if (contactId && this.contactService.userContact.dbId === contactId) {
                                //we've joined the conference from another device, stop ringing
                                myParticipantFound = true;
                                this.logger.debug(`[WebConferenceService] onConferenceMessage -- roomId ${roomId} -- user has joined from another device`);
                                this.sendInnerEvent(WebConferenceServiceInnerEvents.ON_WEBCONFERENCE_HIDE_INVITATION, { roomDbId: roomId });
                            }
                        });
                    }
                }

                //it's a new conference related to ad-hoc
                if (this.adhocConferenceStarting && myParticipantFound) {
                    //create new web conference
                    //and execute the code after that
                    if (!webConferenceSession) {
                        webConferenceSession = new WebConferenceSession(roomId, null);
                        this.webConferenceSessions[roomId] = webConferenceSession;
                    }

                    if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTING) {
                        webConferenceSession.status = BubbleConferenceStatus.CONNECTING;
                        // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                        this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: this.webConferenceSessions[roomId], status: BubbleConferenceStatus.CONNECTING });
                    }

                    this.adhocConferenceStarting = false;
                }
                else {
                    //ignore the next code
                    return true;
                }
            }

            // Handle conference-state
            const conferenceInfoElems = stanzaElem.find('conference-info');
            if (conferenceInfoElems.length) {

                // Handle conference-state
                const conferenceStateElems = stanzaElem.find('conference-state');
                if (conferenceStateElems.length) {
                    const activeConference = $(conferenceStateElems).find('active').text() === 'true';
                    if (!activeConference) {
                        this.logger.info(`[WebConferenceService] onConferenceMessage -- ${webConferenceSession.id} -- has ended`);
                        const bubble = webConferenceSession.bubble;
                        this.removeActiveConferenceSession(webConferenceSession);
                        //the conference no longer exists for this room, clear all data
                        if (bubble) {
                            bubble.conference.sessions = [];
                            bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);
                        }
                        return true;
                    }

                    const lockElem = conferenceStateElems.find("lock");
                    const isLock = (lockElem.text() === "true");

                    let lockedBy = conferenceStateElems.find("locked-by").text();
                    if (!lockedBy) { lockedBy = conferenceStateElems.find("unlocked-by").text(); }

                    if (webConferenceSession.isLocked() !== isLock) {
                        webConferenceSession.setLocked(isLock);
                        if (lockedBy) this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCKSTATE_UPDATE, { roomDbId: webConferenceSession.id, isLock: isLock, lockedBy: lockedBy });
                    }

                    //at join, the recording events are received inside the conference-state
                    const recordingUserIdElem = conferenceInfoElems.find('recording-user-id');
                    if (recordingUserIdElem.length) {
                        webConferenceSession.recordingUserId = recordingUserIdElem.text();
                    }

                    const recordingElem = conferenceInfoElems.find("recording-state");
                    if (recordingElem.length) {
                        const recordingText = recordingElem.text();
                        if (recordingText === "on" || recordingText === "pause") {
                            webConferenceSession.recordingStarted = true;
                            webConferenceSession.currentRecordingState = recordingText;

                            //TO DO to be moved to RXjs
                            this.eventService.publish(recordingText === "on" ? "ON_CONFERENCE_RECORDING_STARTED" : "ON_CONFERENCE_RECORDING_PAUSED", webConferenceSession);
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                        }
                        //manage owner of the recording here
                        //manage conversation events in conferenceRecording service
                    }
                }

                const recordingUserIdElem = conferenceInfoElems.find('recording-user-id');
                if (recordingUserIdElem.length) {
                    webConferenceSession.recordingUserId = recordingUserIdElem.text();
                }

                const recordingElem = conferenceInfoElems.find("record");
                if (recordingElem.length) {
                    const recordingText = recordingElem.text();
                    if (recordingText === "on" || recordingText === "pause" || recordingText === "resume") {
                        webConferenceSession.recordingStarted = true;
                        webConferenceSession.currentRecordingState = recordingText;

                        //TO DO to be moved to RXjs
                        this.eventService.publish(recordingText === "on" ? "ON_CONFERENCE_RECORDING_STARTED" : "ON_CONFERENCE_RECORDING_PAUSED", webConferenceSession);
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                    }
                    else if (recordingText === "off") {
                        webConferenceSession.recordingStarted = false;
                        webConferenceSession.currentRecordingState = "";
                        this.eventService.publish("ON_CONFERENCE_RECORDING_STOPPED", webConferenceSession, (webConferenceSession.recordingUserId === this.contactService.userContact.dbId) /*webConferenceSession.record?.isRecording*/);
                        webConferenceSession.postTranscriptionActivated = false;
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                    }

                    else if (recordingText === "recordingEnding") {
                        //send recording event only if it's my recording
                        if (webConferenceSession.record?.isRecording) {
                            this.logger.info(`[WebConferenceService] onConferenceMessage ${recordingText} -- Warning -- ${webConferenceSession.id}`);
                            this.eventService.publish("ON_CONFERENCE_RECORDING_WILL_STOP");
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                        }

                    }
                    //manage owner of the recording here
                    //manage conversation events in conferenceRecording service

                    const transcriptionElem = conferenceInfoElems.find('transcription');
                    if (transcriptionElem.length) {
                        webConferenceSession.postTranscriptionActivated = true;
                    }
                }

                // Handle conference-info event attribute
                const conferenceInfoEvent = $(conferenceInfoElems[0]).attr('event');
                if (conferenceInfoEvent === 'liveTranscriptionStop') {
                    webConferenceSession.liveTranscriptionUsedBySomeone = false;
                    webConferenceSession.liveTranscriptionActiveLanguages = [];
                    this.logger.info(`[WebConferenceService] onConferenceMessage -- liveTranscription stopped event`);
                }
                else if (conferenceInfoEvent === 'liveTranscriptionStart') {
                    webConferenceSession.liveTranscriptionUsedBySomeone = true;
                }

                const liveTranscriptionElem = conferenceInfoElems.find('live-transcription');
                if (liveTranscriptionElem?.length) {

                    // const transcriptionElem = conferenceInfoElems.find('transcription');
                    // if (transcriptionElem.length && transcriptionElem.text() === 'true') {
                    //     webConferenceSession.liveTranscriptionUsedBySomeone = true;
                    // }

                    const liveTranscriptionLanguagesElem = conferenceInfoElems.find('translation-languages');
                    if (liveTranscriptionLanguagesElem.length) {
                        //get the list of languages available
                        const translationLanguages: string[] = [];
                        liveTranscriptionLanguagesElem.find('translation-language').each(function () {
                            const language = $(this).text();
                            if (language) {
                                translationLanguages.push(language);
                            }
                        });
                        webConferenceSession.liveTranscriptionActiveLanguages = translationLanguages;
                        if (translationLanguages?.length) { webConferenceSession.liveTranscriptionUsedBySomeone = true; }
                        this.logger.info(`[WebConferenceService] onConferenceMessage -- liveTranscription languages received: ${translationLanguages.join(', ')}`);
                    }
                }

                // Handle action
                const action = $(conferenceInfoElems[0]).attr('action');

                if (action === 'unmute') {
                    //we're asked to unmute ourselves
                    const dbId = conferenceInfoElems.find("asked-by").text();
                    //if no asked by, then it's local action
                    if (dbId) this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_UNMUTE_DEMAND_RECEIVED, { roomDbId: webConferenceSession.id, askedBy: dbId });
                }

                if (action === 'raise-hand' || action === 'lower-hand') { // someone has raised/lowered their hand
                    const participantId = stanzaElem.find('user-id').text();
                    const participant = webConferenceSession.getParticipantById(participantId);
                    if (participant) {
                        this.logger.infoEvent(`[WebConferenceService] onConferenceMessage -- ${action} (roomId: ${webConferenceSession.bubble.dbId}, participant: ${participantId})`);
                        participant.raiseHand = action === 'raise-hand';
                        participant.raiseHandDate = (action === 'raise-hand') ? new Date() : null;
                        webConferenceSession.updateRaiseHandParticipants();
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_RAISEHAND_CHANGE, { raiseHand: participant.raiseHand, raiseHandDate: participant.raiseHandDate });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id, participant: participant, action: action });
                    }
                }

                // Handle already raised hands (only after joining a conf)
                if (conferenceInfoElems.find("raise-hands").length) {
                    this.logger.infoEvent(`[WebConferenceService] onConferenceMessage -- All local hands lowered (roomId: ${webConferenceSession.bubble.dbId})`);
                    // first lower all hands
                    webConferenceSession.raiseHandParticipants?.map((participant: BubbleConferenceParticipant) => {
                        participant.raiseHand = false;
                    });

                    // then raise the right ones
                    const raisedHandsElem = conferenceInfoElems.find("raise-hands").find("raise-hand");
                    if (raisedHandsElem.length) {
                        raisedHandsElem.each((__i: number, participantElem: Element) => {
                            const userId = $(participantElem).find('user-id').text();
                            const date = $(participantElem).find('date').text();

                            let contact;
                            if (webConferenceSession.getLocalParticipant().id === userId) {
                                contact = webConferenceSession.getLocalParticipant();
                            } else {
                                contact = webConferenceSession.getParticipantById(userId);
                            }

                            if (contact) {
                                contact.raiseHand = true;
                                contact.raiseHandDate = new Date(date);
                                this.logger.infoEvent(`[WebConferenceService] onConferenceMessage -- Raising participant's hand ${userId} (roomId: ${webConferenceSession.bubble.dbId})`);
                            } else {
                                // not yet existing contact, happens when picking up between devices
                                this.raiseHandTemp.push({ userId: userId, date: date });
                            }
                        });
                    }

                    webConferenceSession.updateRaiseHandParticipants();
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                }
            }

            // Handle session add/update participants event
            let participantAction: string = 'newParticipant';
            let participantElems = stanzaElem.find('participants').find('participant');
            if (participantElems.length === 0) { participantElems = stanzaElem.find('added-participants').find('participant'); participantAction = 'addParticipant'; }
            if (participantElems.length === 0) { participantElems = stanzaElem.find('updated-participants').find('participant'); participantAction = 'updateParticipant'; }

            if (participantElems.length) {
                const participantsPromise = [];
                participantElems.each((__i: number, participantElem: Element) => { participantsPromise.push(this.createSessionParticipantFromElem($(participantElem), webConferenceSession)); });
                const participants: BubbleConferenceParticipant[] = await Promise.all(participantsPromise);
                participants.forEach((participant: BubbleConferenceParticipant) => {
                    if (participant) {
                        if (this.contactService.isUserContact(participant.contact) && !webConferenceSession.localParticipant) {
                            webConferenceSession.localParticipant = participant;
                        }
                        else {
                            webConferenceSession.addOrUpdateParticipant(participant);
                        }
                        const participantId = participant.id === webConferenceSession.localParticipant.id ? 'local' : participant.id;
                        this.logger.info(`[WebConferenceService] onConferenceMessage ${participantAction} -- ${participantId} -- ${webConferenceSession.id}`);

                        if (participantAction === "addParticipant") {
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_ADD, { roomDbId: webConferenceSession.id, participantId: participant.id });
                        }

                        if (participantAction === "updateParticipant" && participantId === "local") {
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCAL_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });
                        }
                    }
                });

                if (participantAction === "newParticipant" || participantAction === "addParticipant") {
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                }
                else if (participantAction === "updateParticipant") {
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });
                }

                webConferenceSession.updateRaiseHandParticipants();
            }

            // Handle session remove participant event
            const removedParticipantElems = stanzaElem.find('removed-participants');
            if (removedParticipantElems.length) {
                let shouldRemoveLocalParticipant = false;
                const participantIdElems = $(removedParticipantElems).find('user-id');
                participantIdElems.each((__i: number, participantIdElem: Element) => {
                    const participantId = $(participantIdElem).text();
                    const participantIndex = webConferenceSession.participants.findIndex((participant: BubbleConferenceParticipant) => { return participant.id === participantId; });
                    if (participantIndex !== -1) {
                        webConferenceSession.participants.splice(participantIndex, 1);
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_REMOVE, { roomDbId: webConferenceSession.id, participantId: participantId });
                    }

                    // WARNING: In SDK case not remove the webConference object, only when the conference is terminated
                    if (webConferenceSession.localParticipant && participantId === webConferenceSession.localParticipant.id) {
                        shouldRemoveLocalParticipant = true;
                    }
                    this.logger.info(`[WebConferenceService] onConferenceMessage removedParticipant -- ${participantId} -- ${webConferenceSession.id}`);
                });

                const participantPhoneElems = $(removedParticipantElems).find('phone-number');
                participantPhoneElems.each((__i: number, participantIdElem: Element) => {
                    const participantId = $(participantIdElem).text();
                    const participantIndex = webConferenceSession.participants.findIndex((participant: BubbleConferenceParticipant) => { return participant.id === participantId; });
                    if (participantIndex !== -1) {
                        webConferenceSession.participants.splice(participantIndex, 1);
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_REMOVE, { roomDbId: webConferenceSession.id, participantId: participantId });
                    }

                    this.logger.info(`[WebConferenceService] onConferenceMessage removedParticipant PSTN -- ${participantId} -- ${webConferenceSession.id}`);
                });

                webConferenceSession.updateRaiseHandParticipants();

                if (shouldRemoveLocalParticipant) {
                    this.removeActiveConferenceSession(webConferenceSession);
                }
                else {
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                }
            }

            // // Handle publishers
            let publisherMode: string = 'publishers';
            let publisherElems = stanzaElem.find('publishers');
            if (publisherElems.length === 0) { publisherElems = stanzaElem.find('added-publishers'); publisherMode = 'addPublisher'; }

            if (publisherElems.length) {
                publisherElems.find('publisher').each((__index: number, publisher: any) => {
                    const publisherElem = $(publisher);
                    const publisherId = publisherElem.find('user-id').text();
                    const resourceId = publisherElem.find('resourceId').text();
                    const mediaType = publisherElem.find('media-type').text();
                    const simulcast = (publisherElem.find("simulcast").text() === "true");
                    if (publisherId === webConferenceSession.localParticipant.id) {
                        if (mediaType === "video") { webConferenceSession.localParticipant.isVideoAvailable = true; }
                        else if (mediaType === "sharing") { webConferenceSession.hasLocalSharing = true; }
                    }
                    else {
                        const participant = webConferenceSession.getParticipantById(publisherId);
                        if (mediaType === 'video') {
                            participant.isVideoAvailable = true;
                            participant.simulcast = simulcast;
                        }
                        else if (mediaType === 'sharing') {
                            //create sharing participant
                            const sharingParticipant = new BubbleConferenceParticipant(participant.id, resourceId);
                            sharingParticipant.contact = participant.contact;
                            sharingParticipant.isSharingParticipant = true;
                            webConferenceSession.setSharingParticipant(sharingParticipant);
                        }
                        else if (mediaType === 'application') {
                        }
                    }

                    this.logger.info(`[WebConferenceService] onConferenceMessage -- ${webConferenceSession.id} -- ${publisherMode} -- ${publisherId} -- ${mediaType}`);
                });

                if (publisherElems.find('publisher').length) {
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD, { roomDbId: webConferenceSession.id });
                }
            }

            const removedPublisherElems = stanzaElem.find('removed-publishers');
            if (removedPublisherElems.length) {
                removedPublisherElems.find('publisher').each((__index: number, publisher: any) => {
                    const publisherElem = $(publisher);
                    const publisherId = publisherElem.find('user-id').text();
                    const mediaType = publisherElem.find('media-type').text();
                    if (publisherId === webConferenceSession.localParticipant.id) {
                        if (mediaType === "video") {
                            webConferenceSession.localParticipant.isVideoAvailable = false;
                            if (webConferenceSession.localParticipant.videoSession) {
                                this.webrtcConfigAndUtils.disableAllStreamsForSession(webConferenceSession.localParticipant.videoSession);
                                webConferenceSession.localParticipant.videoSession.terminate();
                                webConferenceSession.localParticipant.videoSession = null;
                                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: mediaType, action: BubbleConferenceMediaAction.REMOVED });
                                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCALMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType, action: BubbleConferenceMediaAction.REMOVED });
                            }
                        }
                        else if (mediaType === "sharing") {
                            webConferenceSession.hasLocalSharing = false;
                            let sharingSession = webConferenceSession.getLocalSharingSession();
                            if (sharingSession) {
                                this.webrtcConfigAndUtils.disableAllStreamsForSession(sharingSession);
                                sharingSession.terminate();
                                sharingSession = null;

                                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_LOCAL_PARTICIPANT_MEDIA_CHANGE, { mediaType: mediaType, action: BubbleConferenceMediaAction.REMOVED });
                            }
                        }
                    }
                    else {
                        const participant = webConferenceSession.getParticipantById(publisherId);
                        if (mediaType === "video") {
                            participant.isVideoAvailable = false;
                            participant.simulcast = false;
                            if (participant.videoSession) {
                                participant.videoSession.terminate();
                                participant.videoSession = null;
                            }
                        }
                        else if (mediaType === "sharing") {
                            const sharingParticipant = webConferenceSession.getSharingParticipant();
                            if (sharingParticipant && sharingParticipant.id === participant.id) {
                                //remove sharing participant
                                webConferenceSession.setSharingParticipant(null);

                                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, { mediaType: mediaType, action: BubbleConferenceMediaAction.REMOVED, participant });
                            }
                        }
                    }

                    this.logger.info(`[WebinarConferenceService] onConferenceMessage -- ${webConferenceSession.id} -- removePublisher -- ${publisherId} -- ${mediaType}`);
                });

                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_REMOVE, { roomDbId: webConferenceSession.id });
            }

            // Handle silents (so that we can remove the speaker)
            const silentsElems = stanzaElem.find('silents');
            if (silentsElems.length) {
                const silent = silentsElems.find('user-id').text();
                const silentTime = silentsElems.find("talking-time").text();
                if (silent) {
                    webConferenceSession.talker = null;
                    if (silentTime) {
                        const silentTimeValue = silentTime ? parseInt(silentTime, 10) : 0;
                        webConferenceSession.updateTalkingTimeForParticipant(silent, silentTimeValue);
                    }
                }
            }

            // Handle talkers (so that we can add a new speaker)
            const talkerElems = stanzaElem.find('talkers');
            if (talkerElems.length) {
                const talker = talkerElems.find('user-id').text();
                const talkingTime = talkerElems.find("talking-time").text();
                if (talker) {
                    webConferenceSession.lastTalker = talker;
                    webConferenceSession.talker = talker;
                    if (talkingTime) {
                        const talkingTimeValue = talkingTime ? parseInt(talkingTime, 10) : 0;
                        webConferenceSession.updateTalkingTimeForParticipant(talker, talkingTimeValue);
                    }
                }
            }

            // Handle subStreamLevel
            const substreamElems = stanzaElem.find('substream');
            if (substreamElems.length) {
                const publisherId = stanzaElem.find('user-id').text();
                const participant = webConferenceSession.getParticipantById(publisherId);
                if (participant?.videoSession) {
                    participant.subStreamLevel = parseInt($(substreamElems[0]).text(), 10);
                }
            }

            if (silentsElems.length || talkerElems.length) {
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_TALKER_UPDATE, { roomDbId: webConferenceSession.id });
            }

            //manage session-info events
            //for the moment we manage only Phone number contact updates
            const sessionInfo = stanzaElem.find('session-info');
            if (sessionInfo.length) {
                const phoneNumber = stanzaElem.find('phone-number').text();
                const userId = stanzaElem.find('user-id').text();
                const state = stanzaElem.find("session-info").text();
                const participantElem = stanzaElem.find("participant");
                if ((phoneNumber || userId) && state) {
                    //find participant by phone number or ID
                    let participant;

                    if (phoneNumber) { participant = webConferenceSession.getParticipantById(phoneNumber); }
                    if (userId) { participant = webConferenceSession.getParticipantById(userId); }

                    if (!participant && participantElem.length && state !== "hangup") {
                        this.createSessionParticipantFromElem($(participantElem), webConferenceSession)
                            .then((participant) => {
                                if (participant) {
                                    webConferenceSession.addOrUpdateParticipant(participant);

                                    participant.currentPstnState = state;
                                    if (state === "ringing" || state === "in_progress") {
                                        participant.noMicrophone = true;
                                    }
                                    else if (state === "active") {
                                        participant.noMicrophone = false;
                                    }

                                    this.logger.info(`[WebConferenceService] onConferenceMessage pstn participant added -- ${participant.id} -- ${webConferenceSession.id}`);
                                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                                }

                                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });
                            });
                    }

                    if (participant) {
                        participant.currentPstnState = state;
                        if (state === "ringing" || state === "in_progress") {
                            participant.noMicrophone = true;
                        }
                        else if (state === "active") {
                            participant.noMicrophone = false;
                        }
                        else if (state === "hangup") {
                            //we need to remove the participant from the list
                            const participantIndex = webConferenceSession.participants.findIndex((part: BubbleConferenceParticipant) => { return participant.id === part.id; });
                            if (participantIndex !== -1) webConferenceSession.participants.splice(participantIndex, 1);
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                        }
                    }

                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id });
                }
            }

            const services = stanzaElem.find('services');
            if (services.length) {
                services.find('service').each((__index: number, service: any) => {
                    const serviceElem = $(service);
                    const serviceId = serviceElem.find('service-id').text();
                    const serviceType = serviceElem.find("service-type").text();

                    webConferenceSession.addOrUpdateService(serviceId, serviceType);
                    this.logger.info(`[WebinarConferenceService] onConferenceMessage -- ${webConferenceSession.id} -- services`);
                });

                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_SERVICE_UPDATE, { roomDbId: webConferenceSession.id });
            }

            return true;
        }
        catch (error) {
            this.logger.error(`[WebinarConferenceService] onConferenceMessage -- ${error.message}`);
            return true;
        }
    }

    /*
    *
    *   JINGLE EVENTS AND HELPER FUNCTIONS
    *
    */

    public attachRemoteAudioStream(webConferenceSession: WebConferenceSession): void {
        if (webConferenceSession.noAudio) { return; }

        const audioSession = webConferenceSession.getAudioSession();

        //audio session unified plan
        if (audioSession?.remoteStreamsObject["0"]) {
            this.webrtcConfigAndUtils.getBrowserMethodHandler().attachMediaStream([document.getElementById("globalAudioTag")], audioSession.remoteStreamsObject["0"]);
        }

        //check if remote audio streaming is in progress
        const sharingSession = webConferenceSession.getRemoteSharingSession();
        if (sharingSession?.remoteStreamsObject["0"]?.getAudioTracks().length) {
            this.webrtcConfigAndUtils.RTC.attachMediaStreamIfNeeded([document.getElementById("globalAudioTagSharing")], sharingSession.remoteStreamsObject["0"], sharingSession.sid);
        }
    }

    private getWebConferenceByJingleSessionId(sessionId: string, confId: string = null): WebConferenceSession {
        let conferenceId = confId;
        if (!conferenceId) {
            const session = this.xmppService.connection.jingle.sessions[sessionId];
            if (!session) { return null; }
            conferenceId = session.conference_id;
        }
        return this.webConferenceSessions[conferenceId];
    }

    private onJingleUpdateStream(webConferenceSession: WebConferenceSession, medias: any) {
        //ignore events not for this conference
        if (medias?.audio) {
            this.attachRemoteAudioStream(webConferenceSession);
        }
    }

    private webrtcConfigAndUtilsEvent(event: RBEvent) {
        try {
            if (event.name === 'ON_CHROME_STOP_SCREEN_SHARING_TRIGGERED') {
                const webConferenceSession = this.getActiveWebConferenceSession();
                if (webConferenceSession?.localSharingSession) { this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.SHARING); }
            }
        }
        catch (error) {
            this.logger.debug(`[WebConferenceService] webrtcConfigAndUtilsEvent error -- ${event.name} -- ${error}`);
        }
    }

    private onJingleMessage(event: RBEvent): void {
        try {
            const conferenceSession = this.getWebConferenceByJingleSessionId(event.data.sid, event.data.confId);
            if (conferenceSession) {
                switch (event.name) {
                    case 'jingleSessionInitiate': this.onJingleSessionInvite(conferenceSession, event.data.sid); break;
                    case 'jingleConnectionStateChange': this.onConfSessionConnectionStateUpdate(conferenceSession, event.data.session); break;
                    case 'jingleSessionTerminate': this.onSessionTerminated(conferenceSession, event.data.sid, event.data.reason); break;
                    case 'jingleUpdateStream': this.onJingleUpdateStream(conferenceSession, event.data.medias); break;
                    case 'jingleError': this.onJingleErrorEvent(conferenceSession, event.data.sid, event.data.error); break;
                    case 'resubscriptionNeeded': this.onJingleResubscriptionNeededEvent(conferenceSession, event.data.sid); break;
                    case 'webrtcStatsEvent.jingle': this.onJingleSessionStats(conferenceSession, event.data.sid, event.data.data); break;
                    case 'videoQualityChangeEvent': this.onJingleVideoQualityChangeEvent(conferenceSession, event.data.sid, event.data.decreaseQuality); break;
                    default:
                        break;
                }
            }
        }
        catch (error) {
            this.logger.info(`[WebConferenceService] onJingleMessage -- failure -- ${event.name}`);
        }
    }

    private onJingleSessionInvite(webConferenceSession: WebConferenceSession, sessionId: string): void {

        // Ignore Application type messages
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session.remoteType === 'application') return;

        this.logger.info(`[WebConferenceService] onJingleSessionInvite -- ${webConferenceSession.id} -- ${sessionId}`);

        session.localType = session.remoteType;
        session.sendAnswer();
        this.statsIntervals.push(this.webrtcConfigAndUtils.getStatsForSession(session, 6000));

        //check if this is the dynamic feed sesion
        if (session.localType === "video" && !session.isInitiator && !session.publisherId) {
            this.logger.info(`[WebConferenceService] onJingleSessionInvite -- ${webConferenceSession.id} -- ${sessionId} -- marked as dynamic feed`);
            webConferenceSession.dynamicFeedSession = session;
        }

        this.logger.info(`[WebConferenceService] onJingleSessionInvite -- accept -- ${webConferenceSession.id} -- ${sessionId}`);
    }

    private onSessionTerminated(webConferenceSession: WebConferenceSession, sessionId: string, reason: string = "") {
        this.logger.info(`[WebConferenceService] onJingleMessage -- onSessionTerminated -- ${webConferenceSession.id} -- ${sessionId} -- with reason ${reason}`);

        if (reason === "multi-device") {
            this.removeActiveConferenceSession(webConferenceSession);
        }
        // WARNING: In SDK case not remove the webConference object, only when the conference is terminated
        else if (webConferenceSession.audioSession && webConferenceSession.audioSession.sid === sessionId) {
            this.removeActiveConferenceSession(webConferenceSession);
        }
        else {
            const session = this.xmppService.connection.jingle.sessions[sessionId];
            if (session) {
                if (session.isInitiator) { this.removeMediaFromWebConference(webConferenceSession, session.localType); }
                else {
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                        mediaType: session.remoteType,
                        action: BubbleConferenceMediaAction.REMOVED,
                        participant: session.publisherId ? webConferenceSession.getParticipantById(session.publisherId) : undefined
                    });
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: session.remoteType, action: BubbleConferenceMediaAction.REMOVED });
                }
            }
            else {
                //check if session is part of any of the remote participants
                webConferenceSession.participants.forEach((webConferenceParticipant) => {
                    if (webConferenceParticipant?.videoSession && webConferenceParticipant.videoSession.sid === sessionId) {
                        webConferenceParticipant.videoSession.terminate();
                        webConferenceParticipant.videoSession = null;
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                            mediaType: BubbleConferenceMedia.VIDEO,
                            action: BubbleConferenceMediaAction.REMOVED,
                            participant: webConferenceParticipant
                        });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.REMOVED });
                    }
                });

                //check if this is local sharing session
                if (webConferenceSession.localSharingSession?.sid === sessionId) {
                    this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.SHARING);
                }
            }
        }
        this.raiseHandTemp = [];
    }

    private onJingleSessionStats(webConferenceSession: WebConferenceSession, sessionId: string, data: WebrtcStatisticsData) {
        if (webConferenceSession.audioSession && webConferenceSession.audioSession.sid === sessionId && data.audio?.received) {
            webConferenceSession.networkQuality = data.audio.received.networkQualityValue;
            this.eventService.publish("ON_CONFERENCE_NETWORK_UPDATE_EVENT", webConferenceSession, "networkQuality", webConferenceSession.networkQuality);
        }

        if (webConferenceSession.isMediaEchoTestConference) {
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_ECHOTEST_STATS_UPDATE, { roomDbId: webConferenceSession.id, stats: data });
        }
    }

    private onJingleVideoQualityChangeEvent(webConferenceSession: WebConferenceSession, sessionId: string, decreaseQuality: boolean) {
        //ignore event based on settings service
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_MODULATE_VIDEO_QUALITY)) {
            this.logger.info(`[WebConferenceService] onJingleVideoQualityChangeEvent -- ignored -- profile not enabled`);
            return;
        }

        const session = this.xmppService.connection.jingle.sessions[sessionId];

        if (!session) {
            this.logger.info(`[WebConferenceService] onJingleVideoQualityChangeEvent -- ignored -- session not found`);
            return;
        }

        if (!session.isInitiator && session.remoteType === "video") {
            const participant = webConferenceSession.getParticipantById(session.publisherId);
            if (participant) {
                this.webConferenceHelper.updateParticipantVideoOnQualityChange(webConferenceSession, participant, decreaseQuality, session);
            }
        }
    }

    private onJingleResubscriptionNeededEvent(webConferenceSession: WebConferenceSession, sessionId: string): void {
        //if we dont have network, ignore this
        if (!this.connected) {
            return;
        }

        this.logger.warn(`[WebConferenceService] onJingleResubscriptionNeededEvent -- ${webConferenceSession.id} -- session sid ${sessionId}`);
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        if (session && !session.isInitiator) {
            if (session.remoteType === "video") {
                this.logger.info("[WebConferenceService] -- onJingleResubscriptionNeededEvent -- try to reconnect the remote video session -- " + session.sid);
                //terminate and resubscribe to session
                this.xmppService.connection.jingle.terminate(session.sid);

                //update the stats for the session before removing
                this.updateStatisticsForSession(webConferenceSession, session.sid);
                delete this.webrtcConfigAndUtils.callsStats[session.sid];

                if (webConferenceSession.gridServiceSubscription?.videoSession && webConferenceSession.gridServiceSubscription.videoSession.sid === session.sid) {
                    this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the grid / mozaic service session -- " + session.sid);
                    webConferenceSession.gridServiceSubscription = null;
                    webConferenceSession.services.forEach((service) => {
                        if (service.name === "video-compositor") {
                            this.subscribeToService(webConferenceSession, service.id);
                        }
                    });
                }
                if (!session.publisherId) {
                    this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the dynamic feed session -- " + session.sid);
                    webConferenceSession.dynamicFeedSession = null;
                    this.subscribeToDynamicFeed(webConferenceSession);
                }
                else {
                    //we should resubscribe to this session
                    const participant = webConferenceSession.getParticipantById(session.publisherId);

                    if (participant.videoSession) {
                        participant.videoSession = null;
                        //update the stats for the session before removing
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                            mediaType: BubbleConferenceMedia.VIDEO,
                            action: BubbleConferenceMediaAction.REMOVED,
                            participant: participant
                        });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.REMOVED });
                        this.subscribeToParticipantVideo(webConferenceSession, participant);
                    }
                }
            }
            if (session.remoteType === "sharing") {
                this.logger.info("[WebConferenceService] -- onJingleResubscriptionNeededEvent -- try to reconnect the remote sharing session -- " + session.sid);
                this.xmppService.connection.jingle.terminate(session.sid, "icefailed");
                const sharingParticipant = webConferenceSession.getRemoteSharingParticipant();
                if (sharingParticipant?.videoSession) {
                    sharingParticipant.videoSession = null;
                    //update the stats for the session before removing
                    this.updateStatisticsForSession(webConferenceSession, session.sid);
                    delete this.webrtcConfigAndUtils.callsStats[session.sid];

                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                        mediaType: BubbleConferenceMedia.SHARING,
                        action: BubbleConferenceMediaAction.REMOVED,
                        participant: sharingParticipant
                    });
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.REMOVED });
                }
            }
        }
    }

    private onJingleErrorEvent(webConferenceSession: WebConferenceSession, sessionId: string, error: any) {
        this.logger.error(`[WebConferenceService] onJingleErrorEvent -- ${webConferenceSession.id} -- ${JSON.stringify(error)}`);

        if (webConferenceSession.audioSession.sid === sessionId) {
            this.leaveWebConference(webConferenceSession);
        }
        else if (webConferenceSession.localParticipant.videoSession && webConferenceSession.localParticipant.videoSession.sid === sessionId) {
            this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.VIDEO);
        }
        else if (webConferenceSession.localSharingSession && webConferenceSession.localSharingSession.sid === sessionId) {
            this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.SHARING);
        }
        else {
            const session = this.xmppService.connection.jingle.sessions[sessionId];
            if (session && !session.isInitiator) {
                if (session.remoteType === "video") {
                    this.logger.info("[WebConferenceService] -- onJingleErrorEvent -- try to reconnect the remote video session -- " + session.sid);
                    //terminate and resubscribe to session
                    this.xmppService.connection.jingle.terminate(session.sid);

                    //we should resubscribe to this session
                    const participant = webConferenceSession.getParticipantById(session.publisherId);

                    if (participant.videoSession) {
                        participant.videoSession = null;
                        //update the stats for the session before removing
                        this.updateStatisticsForSession(webConferenceSession, session.sid);
                        delete this.webrtcConfigAndUtils.callsStats[session.sid];
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                            mediaType: BubbleConferenceMedia.VIDEO,
                            action: BubbleConferenceMediaAction.REMOVED,
                            participant: participant
                        });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.REMOVED });

                        this.subscribeToParticipantVideo(webConferenceSession, participant);
                    }

                }
                if (session.remoteType === "sharing") {
                    this.logger.info("[WebConferenceService] -- onJingleErrorEvent -- try to reconnect the remote sharing session -- " + session.sid);
                    this.xmppService.connection.jingle.terminate(session.sid, "icefailed");
                    const sharingParticipant = webConferenceSession.getRemoteSharingParticipant();
                    if (sharingParticipant?.videoSession) {
                        sharingParticipant.videoSession = null;
                        //update the stats for the session before removing
                        this.updateStatisticsForSession(webConferenceSession, session.sid);
                        delete this.webrtcConfigAndUtils.callsStats[session.sid];
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                            mediaType: BubbleConferenceMedia.SHARING,
                            action: BubbleConferenceMediaAction.REMOVED,
                            participant: sharingParticipant
                        });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.REMOVED });
                    }
                }
            }
        }
    }

    private async onConfSessionConnectionStateUpdate(webConferenceSession: WebConferenceSession, session: JingleSession): Promise<any> {
        try {
            // Ignore "application" events
            if (session?.remoteType === 'application') return;
            if (!session?.peerconnection) {
                this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate -- peerConnection does not exist anymore`);
                return;
            }

            if (session.peerconnection.signalingState !== "stable") {
                this.logger.info(`[WebConferenceService] onSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: signalingState ${session.peerconnection.signalingState} iceConnectionState ${session.peerconnection.iceConnectionState} and connectionState ${session.peerconnection.connectionState} -- event is ignored as signalingState is not stable`);
                return;
            }

            const state = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;

            this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: signalingState ${session.peerconnection.signalingState} | iceConnectionState ${session.peerconnection.iceConnectionState} | connectionState ${session.peerconnection.connectionState}`);

            //check for connecting state
            if (session.peerconnection.iceConnectionState === "checking") {
                //start timer to check if session is blocked after 20 sec
                setTimeout(() => {

                    if (webConferenceSession && session?.peerconnection) {
                        const newState = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;
                        if (newState === "connected" || newState === "completed") return;

                        this.logger.debug(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- session is blocked with connectionState ${newState}`);
                        if (session.localType === "audio" || ((session.localType === "video" || session.localType === "sharing") && session.isInitiator)) {
                            session.connection = this.xmppService.connection;
                            session.sendTransportReplace();
                        }
                        else {
                            this.onIceConnectionFailedForSession(webConferenceSession, session);
                        }
                    }
                }, 20000);
            }

            if (session.peerconnection.iceConnectionState === "disconnected") {
                this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- session is in ${session.peerconnection.iceConnectionState}, start reconnect mecanism`);
                this.onIceConnectionDisconnectedForSession(webConferenceSession, session);
                return;
            }

            if (session.peerconnection.iceConnectionState === "failed") {
                this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- session is in ${session.peerconnection.iceConnectionState}, start reconnect mecanism`);
                this.onIceConnectionFailedForSession(webConferenceSession, session);
                return;
            }

            if (state === "connected" || state === "completed") {
                if (session.localType === 'audio') {
                    this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: audio session established`);
                    webConferenceSession.status = BubbleConferenceStatus.CONNECTED;
                    webConferenceSession.startDuration();
                    this.attachRemoteAudioStream(webConferenceSession);
                    // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
                    this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.CONNECTED });
                }

                if (session.localType === 'sharing') {

                    //in case of sharing session, we need to check if the participants are created or otherwise wait
                    if (!webConferenceSession.localParticipant) {
                        this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- local sharing session established but we dont have the participants ready, wait for 1sec`);
                        setTimeout(() => {
                            this.onConfSessionConnectionStateUpdate(webConferenceSession, session);
                        }, 1000);
                        return;
                    }
                    // Handle local participant events
                    if (!session.publisherId || session.publisherId === webConferenceSession.localParticipant.id) {
                        this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: local sharing session established`);
                        this.applyConstraintsToSharingStream(session);
                    }
                    // Handle remote participant events
                    else {
                        let sharingParticipant = webConferenceSession.getSharingParticipant();

                        if (!sharingParticipant) {
                            const participant = webConferenceSession.getParticipantById(session.publisherId);

                            if (!participant) {
                                //participant is missing, so wait for him
                                this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- remote sharing session by ${session.publisherId} established but we dont have the participants ready, wait for 1sec`);
                                setTimeout(() => {
                                    this.onConfSessionConnectionStateUpdate(webConferenceSession, session);
                                }, 1000);
                                return;
                            }

                            //create new participant
                            sharingParticipant = new BubbleConferenceParticipant(participant.id);
                            sharingParticipant.contact = participant.contact;
                            sharingParticipant.isSharingParticipant = true;
                            webConferenceSession.setSharingParticipant(sharingParticipant);
                        }

                        sharingParticipant.videoSession = session;

                        webConferenceSession.updatePinnedParticipant(webConferenceSession.getSharingParticipant(), "sharing");

                        if (webConferenceSession.disableIncomingSharing) {
                            webConferenceSession.getRemoteSharingSession()?.sendHold(true);
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                                mediaType: BubbleConferenceMedia.SHARING,
                                action: BubbleConferenceMediaAction.HOLD,
                                participant: sharingParticipant
                            });
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.HOLD });
                        }
                        else {
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                                mediaType: BubbleConferenceMedia.SHARING,
                                action: BubbleConferenceMediaAction.ADDED,
                                participant: sharingParticipant
                            });
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.ADDED });
                        }

                        this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id} -- remote sharing session by ${sharingParticipant.id} established`);
                    }

                    //check if we need to add audio streams to global element
                    if (session?.remoteStreamsObject["0"]?.getAudioTracks().length) {
                        this.attachRemoteAudioStream(webConferenceSession);
                    }

                }
                else if (session.localType === 'video') {
                    if (session.publisherId === webConferenceSession.localParticipant.id || session.sid === webConferenceSession.localParticipant.videoSession?.sid) {
                        this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: local video session established`);
                    }
                    else if (!session.publisherId) {
                        this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: dynamicFeed video session established`);
                        webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                            mediaType: BubbleConferenceMedia.DYNAMIC_FEED,
                            action: BubbleConferenceMediaAction.ADDED
                        });
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.DYNAMIC_FEED, action: BubbleConferenceMediaAction.ADDED });
                    }
                    else {
                        const participant = webConferenceSession.getParticipantById(session.publisherId);
                        if (participant) {
                            participant.isVideoAvailable = true;
                            participant.videoSession = session;
                            participant.subscriptionInProgress = false;
                        }

                        //check if new session is related to the grid 49 service
                        if (webConferenceSession?.gridServiceSubscription && webConferenceSession.gridServiceSubscription.sessionId === session.sid) {
                            this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: grid/mozaic service session established`);
                            webConferenceSession.gridServiceSubscription.videoSession = session;
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                                mediaType: BubbleConferenceMedia.MOZAIC,
                                action: BubbleConferenceMediaAction.ADDED
                            });
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.MOZAIC, action: BubbleConferenceMediaAction.ADDED });
                        }
                        else {
                            this.logger.info(`[WebConferenceService] onConfSessionConnectionStateUpdate for ${session.sid} in conf ${webConferenceSession.id}: remote video session by ${session.publisherId} established`);
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                                mediaType: BubbleConferenceMedia.VIDEO,
                                action: BubbleConferenceMediaAction.ADDED,
                                participant: participant
                            });
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.VIDEO, action: BubbleConferenceMediaAction.ADDED });
                        }
                    }
                }

                this.sendEvent("peerConnectionEstablished", { sessionId: session.sid });
            }
        }
        catch (error) {
            this.logger.error(`[WebConferenceService] onConfSessionConnectionStateUpdatefor ${session.sid} error -- ${error} ${error?.message}`);
        }
    }

    /**
     * Reconnects all the audio / video / sharing jingle sessions related to the active conference session (if any)
     */
    private async reconnectActiveConferenceSession() {
        const webConferenceSession = this.getActiveWebConferenceSession();
        if (!webConferenceSession) { return; }
        if (!webConferenceSession.localParticipant) { this.removeActiveConferenceSession(webConferenceSession); return; }

        const isActive = await this.updateWebConferenceAfterReconnection(webConferenceSession);
        if (!isActive) { return; }
        this.logger.info("[WebConferenceService] -- reconnectAllActiveConferenceSessions -- try to reconnect the audio session");
        this.sendTransportReplaceForSession(webConferenceSession, webConferenceSession.audioSession, 500);

        if (webConferenceSession.localParticipant.videoSession) {
            this.logger.info("[WebConferenceService] -- reconnectAllActiveConferenceSessions -- try to reconnect the video session");
            this.sendTransportReplaceForSession(webConferenceSession, webConferenceSession.localParticipant.videoSession, 1500);
        }

        if (webConferenceSession.localSharingSession) {
            this.logger.info("[WebConferenceService] -- reconnectAllActiveConferenceSessions -- try to reconnect the sharing session");
            this.sendTransportReplaceForSession(webConferenceSession, webConferenceSession.localSharingSession, 2500);
        }

        //reconnect all distant video session
        setTimeout(async () => {
            if (!webConferenceSession.haveJoined) {
                this.logger.info("[WebConferenceService] -- reconnectActiveConferenceSession -- web conf is no longer active");
                return;
            }

            const sharingParticipant = webConferenceSession.getRemoteSharingParticipant();
            if (sharingParticipant?.videoSession?.peerconnection) {
                const state = sharingParticipant.videoSession.peerconnection.connectionState ? sharingParticipant.videoSession.peerconnection.connectionState : sharingParticipant.videoSession.peerconnection.iceConnectionState;
                if (sharingParticipant.videoSession.peerconnection.signalingState === "stable"
                    && (state === "failed" || state === "disconnected")) {
                    this.onIceConnectionFailedForSession(webConferenceSession, sharingParticipant.videoSession);
                }
            }

            if (webConferenceSession.dynamicFeedSession?.peerconnection) {
                const state = webConferenceSession.dynamicFeedSession.peerconnection.connectionState ? webConferenceSession.dynamicFeedSession.peerconnection.connectionState : webConferenceSession.dynamicFeedSession.peerconnection.iceConnectionState;
                if (webConferenceSession.dynamicFeedSession.peerconnection.signalingState === "stable"
                    && (state === "failed" || state === "disconnected")) {
                    this.onIceConnectionFailedForSession(webConferenceSession, webConferenceSession.dynamicFeedSession);
                }
            }

            if (webConferenceSession.gridServiceSubscription?.videoSession?.peerconnection) {
                const state = webConferenceSession.gridServiceSubscription.videoSession.peerconnection.connectionState ? webConferenceSession.gridServiceSubscription.videoSession.peerconnection.connectionState : webConferenceSession.gridServiceSubscription.videoSession.peerconnection.iceConnectionState;

                if (webConferenceSession.gridServiceSubscription.videoSession.peerconnection.signalingState === "stable"
                    && (state === "failed" || state === "disconnected")) {
                    this.onIceConnectionFailedForSession(webConferenceSession, webConferenceSession.gridServiceSubscription.videoSession);
                }
            }

            webConferenceSession.participants.forEach((participant) => {
                if (participant.videoSession?.peerconnection) {
                    // NB For some reason some of the peerconnections remain in disconnected state even though the ICE FAILED is seen in webrtc internals
                    //so in case of reconnection, we take also the disconnected state (which is not the case in case of ice change when we have the network)
                    const state = participant.videoSession.peerconnection.connectionState ? participant.videoSession.peerconnection.connectionState : participant.videoSession.peerconnection.iceConnectionState;
                    if (participant.videoSession.peerconnection.signalingState === "stable" && (state === "failed" || state === "disconnected")) {
                        this.onIceConnectionFailedForSession(webConferenceSession, participant.videoSession);
                    }
                }
            });
        }, 4000);
    }

    /**
     * This event occurs when the ICE connection for a given webrtc session goes to "FAILED" state
     * @param webConferenceSession - the conference session impacted
     * @param session - the jingle session
     */
    private onIceConnectionFailedForSession(webConferenceSession: WebConferenceSession, session: any) {
        if (session.localType === "audio" || ((session.localType === "video" || session.localType === "sharing") && session.isInitiator)) {
            this.onIceConnectionDisconnectedForSession(webConferenceSession, session);
        }
        else if (this.connected && !session.isInitiator) {
            if (session.remoteType === "video") {
                this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the remote video session -- " + session.sid);
                //terminate and resubscribe to session
                this.xmppService.connection.jingle.terminate(session.sid);
                //update the stats for the session before removing
                this.updateStatisticsForSession(webConferenceSession, session.sid);
                delete this.webrtcConfigAndUtils.callsStats[session.sid];

                if (webConferenceSession.gridServiceSubscription?.videoSession && webConferenceSession.gridServiceSubscription.videoSession.sid === session.sid) {
                    this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the grid / mozaic service session -- " + session.sid);
                    webConferenceSession.gridServiceSubscription = null;
                    webConferenceSession.services.forEach((service) => {
                        if (service.name === "video-compositor") {
                            this.subscribeToService(webConferenceSession, service.id);
                        }
                    });
                }
                else if (!session.publisherId) {
                    this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the dynamic feed session -- " + session.sid);
                    webConferenceSession.dynamicFeedSession = null;
                    this.subscribeToDynamicFeed(webConferenceSession);
                }
                else {
                    //we should resubscribe to this session
                    const participant = webConferenceSession.getParticipantById(session.publisherId);
                    participant.videoSession = null;
                    this.subscribeToParticipantVideo(webConferenceSession, participant);
                }
            }

            if (session.remoteType === "sharing") {
                this.logger.info("[WebConferenceService] -- onIceConnectionFailedForSession -- reconnect the remote sharing session -- " + session.sid);
                this.xmppService.connection.jingle.terminate(session.sid, "icefailed");
                const sharingParticipant = webConferenceSession.getRemoteSharingParticipant();
                if (sharingParticipant?.videoSession) { sharingParticipant.videoSession = null; }

                //update the stats for the session before removing
                this.updateStatisticsForSession(webConferenceSession, session.sid);
                delete this.webrtcConfigAndUtils.callsStats[session.sid];
            }
        }
    }

    /**
     * This event occurs when the ICE connection for a given webrtc session goes to "disconnected" stat
     * @param webConference - the conference session impacted
     * @param session - the jingle session
     */
    private onIceConnectionDisconnectedForSession(webConference: WebConferenceSession, session: any) {
        if (session.localType === "audio") {
            webConference.status = BubbleConferenceStatus.RECONNECTING;
            // webConference.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConference, status: BubbleConferenceStatus.RECONNECTING });

            if (this.connected) {
                // if we still have an active connection, send transport-replace
                this.sendTransportReplaceForSession(webConference, session, 5000);
            }
        }
        else if ((session.localType === "video" || session.localType === "sharing") && session.isInitiator) {
            if (this.connected) {
                // if we still have an active connection, send transport-replace
                this.sendTransportReplaceForSession(webConference, session, 8000);
            }
        }
    }

    /**
     * This event occurs when the ICE connection for a given webrtc session goes to "disconnected" state
     * @param webConferenceSession - the web conference session impacted
     * @param session - the jingle session to reconnect
     * @param timeout - the timeout in ms to wait before doing any action. Default value is 1000ms
     */
    private sendTransportReplaceForSession(webConferenceSession: WebConferenceSession, session: any, timeout: number = 1000) {
        if (!session?.sid) {
            this.logger.warn("[WebConferenceService] sendTransportReplaceForSession missing session !");
            return;
        }
        if (!session.peerconnection) {
            this.logger.warn("[WebConferenceService] sendTransportReplaceForSession missing peerconnection for session !");
            return;
        }

        const connectionState = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;

        if (session.peerconnection && session.peerconnection.signalingState === "stable" && (connectionState !== "failed" && connectionState !== "disconnected")) {
            this.logger.info("[WebConferenceService] sendTransportReplaceForSession not needed for " + session.sid);
        } else if (this.reconnectionsInProgress[session.sid]) {
            this.logger.info("[WebConferenceService] sendTransportReplaceForSession already reconnecting for session " + session.sid);
        } else {
            this.reconnectionsInProgress[session.sid] = true;

            setTimeout(async () => {
                this.reconnectionsInProgress[session.sid] = false;
                delete this.reconnectionsInProgress[session.sid];

                if (this.connected && session && session.peerconnection && session.peerconnection.signalingState === "stable" && (session.peerconnection.iceConnectionState === "failed" || session.peerconnection.iceConnectionState === "disconnected")) {
                    this.logger.info("[WebConferenceService] sendTransportReplaceForSession for session " + session.sid);

                    try {
                        session.connection = this.xmppService.connection;
                        await session.sendTransportReplaceWithAwait();
                    } catch (error) {
                        await this.sendTransportReplaceForSession(webConferenceSession, session);
                    }

                    //set the state to busy in case we've lost it during the loss of network
                    const state = (webConferenceSession?.localSharingSession) ? "sharing" : "audio";
                    this.contactService.setBusyState("dnd", state);
                }
                else if (this.connected && session && session.peerconnection && session.peerconnection.iceConnectionState === "closed") {
                    if (session.localType === "audio") {
                        this.leaveWebConference(webConferenceSession);
                    }
                    else if ((session.localType === "video" || session.localType === "sharing") && session.isInitiator) {
                        this.removeMediaFromWebConference(webConferenceSession, session.localType);
                    }
                }
                else {
                    this.logger.info("[WebConferenceService] sendTransportReplaceForSession for session " + session.sid + " is not send ...");
                }
            }, timeout);
        }
    }

    /**
     * This method is used to check the current state of the conference and update the local conference
     * @param webConferenceSession - the web conference session impacted
     */
    private async updateWebConferenceAfterReconnection(webConferenceSession: WebConferenceSession): Promise<boolean> {
        const confData = await this.makeSnapshotByRoomDbId(webConferenceSession.bubble.dbId);

        if (confData.active) {

            //if my participant is no longer in the conference (device switch), then leave the conference
            let hasSwitchedDevice = false;
            confData.participants.forEach((participant) => {
                if (participant.userId === this.contactService.userContact.dbId) {
                    //check if it's the same resource ID
                    if (participant.resourceId && participant.jid_im) {
                        const fullJID = participant.jid_im + '/' + participant.resourceId;
                        if (fullJID !== this.contactService.userContact.fullJid) {
                            hasSwitchedDevice = true;
                        }
                    }
                }
            });

            if (hasSwitchedDevice) {
                this.removeActiveConferenceSession(this.webConferenceSessions[webConferenceSession.id]);
                return false;
            }

            const participantsPromise = [];
            confData.participants.forEach((participant) => { participantsPromise.push(this.createSessionParticipantFromSnapshotData(participant)); });
            const participants: BubbleConferenceParticipant[] = await Promise.all(participantsPromise);
            participants.forEach((participant: BubbleConferenceParticipant) => {
                if (participant) {
                    const confParticipant = webConferenceSession.getParticipantById(participant.id);
                    const oldVideo = confParticipant?.isVideoAvailable;

                    //check if this is me
                    if (participant.id === webConferenceSession.localParticipant.id) {
                        //check if sharing or video have changed
                        if (!participant.isVideoAvailable && confParticipant.isVideoAvailable) {
                            this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.VIDEO);
                        }
                        else if (!participant.isSharingParticipant && webConferenceSession.hasLocalSharing) {
                            this.removeMediaFromWebConference(webConferenceSession, BubbleConferenceMedia.SHARING);
                        }
                    }
                    else {
                        //not my participant

                        //check if participant is sharing participant
                        const sharingParticipant = webConferenceSession.getSharingParticipant();
                        if (sharingParticipant && sharingParticipant.id === participant.id && !participant.isSharingParticipant) {
                            //remove sharing
                            webConferenceSession.setSharingParticipant(null);
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_REMOVE, { roomDbId: webConferenceSession.id });
                            this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection old sharing stream removed for " + participant.id);
                        }
                        else if (!sharingParticipant && participant.isSharingParticipant) {
                            // add sharing to existing or new participant
                            const sharingParticipant = new BubbleConferenceParticipant(participant.id);
                            sharingParticipant.contact = participant.contact;
                            sharingParticipant.isSharingParticipant = true;
                            webConferenceSession.setSharingParticipant(sharingParticipant);
                            this.subscribeToParticipantVideo(webConferenceSession, sharingParticipant, BubbleConferenceMedia.SHARING);
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD, { roomDbId: webConferenceSession.id });
                            this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection new sharing stream added from " + participant.id);
                        }

                        webConferenceSession.addOrUpdateParticipantFromSnapshot(participant);

                        //new participant is added, send event and subscribe if needed
                        if (!confParticipant) {
                            //send update
                            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                            if (participant.isVideoAvailable) {
                                // add video to new participant
                                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD, { roomDbId: webConferenceSession.id });
                                this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection new video stream added from " + participant.id);
                            }
                        }
                        else if (oldVideo !== participant.isVideoAvailable) {
                            if (participant.isVideoAvailable) {
                                // add video to existing participant
                                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD, { roomDbId: webConferenceSession.id });
                                this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection new video stream added from " + participant.id);
                            } else {
                                // remove video
                                confParticipant.simulcast = false;
                                if (confParticipant.videoSession) {
                                    confParticipant.videoSession.terminate();
                                    confParticipant.videoSession = null;
                                }
                                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_REMOVE, { roomDbId: webConferenceSession.id });
                                this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection old video stream removed for " + participant.id);
                            }
                        }
                    }
                }
            });

            // Check if someone is gone
            const existingParticipants = webConferenceSession.getParticipants();
            //as we get only the first 100 participants, do not clean (we should do several snapshots..)
            if (confData.total <= 100) {
                const participantsToRemove = existingParticipants.filter(function (oldPart) {
                    return !participants.some((newPart) => {
                        return oldPart.id === newPart.id;
                    });
                });

                participantsToRemove?.forEach((toRemove) => {
                    if (toRemove.isVideoAvailable) {
                        toRemove.simulcast = false;
                        if (toRemove.videoSession) {
                            toRemove.videoSession.terminate();
                            toRemove.videoSession = null;
                        }
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_REMOVE, { roomDbId: webConferenceSession.id });
                        this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection old video stream removed for " + toRemove.id);
                    }

                    if (webConferenceSession.getSharingParticipant()?.id === toRemove.id) {
                        webConferenceSession.setSharingParticipant(null);
                        this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_REMOVE, { roomDbId: webConferenceSession.id });
                        this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection old sharing stream removed for " + toRemove.id);
                    }

                    const participantIndex = webConferenceSession.participants.findIndex((participant: BubbleConferenceParticipant) => { return participant.id === toRemove.id; });
                    if (participantIndex !== -1) webConferenceSession.participants.splice(participantIndex, 1);
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
                    this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
                    this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection old participant removed from conf: " + toRemove.id);
                });
            }

            // lower all hands
            webConferenceSession.raiseHandParticipants.map((participant: BubbleConferenceParticipant) => {
                participant.raiseHand = false;
            });
            // raise correct hands
            if (confData.raiseHands) {
                confData.raiseHands.forEach((participant) => {
                    const webConfParticipant = webConferenceSession.getParticipantById(participant.userId);
                    if (webConfParticipant) { webConfParticipant.raiseHand = true; }
                });
            }

            if (confData.record) {
                webConferenceSession.recordingUserId = confData.recordOwner;
                webConferenceSession.currentRecordingState = confData.record;
                if (confData.record === "off" && webConferenceSession.recordingStarted) {
                    webConferenceSession.recordingStarted = false;
                    webConferenceSession.currentRecordingState = "";
                    this.eventService.publish("ON_CONFERENCE_RECORDING_STOPPED", webConferenceSession, (webConferenceSession.recordingUserId === this.contactService.userContact.dbId) /*webConferenceSession.record?.isRecording*/);
                    webConferenceSession.postTranscriptionActivated = false;
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                }
                else if (confData.record !== "off" && !webConferenceSession.recordingStarted) {
                    webConferenceSession.recordingStarted = true;
                    this.eventService.publish(confData.record === "on" ? "ON_CONFERENCE_RECORDING_STARTED" : "ON_CONFERENCE_RECORDING_PAUSED", webConferenceSession);
                    webConferenceSession.sendEvent(BubbleConferenceEvents.ON_RECORDING_UPDATE);
                }
            }

            //update live transcription state
            if (confData.liveTranscription) {
                webConferenceSession.liveTranscriptionActiveLanguages = confData.liveTranscription.translationLanguages || [];
            }

            webConferenceSession.updateRaiseHandParticipants();
            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
        }
        else {
            this.logger.info("[WebConferenceService] updateWebConferenceAfterReconnection conf is no longer active: " + webConferenceSession.id);
            //conference is no longer active, remove it
            // delete this.webConferenceSessions[webConferenceSession.bubble.dbId];
            this.removeActiveConferenceSession(this.webConferenceSessions[webConferenceSession.id]);
            webConferenceSession.status = BubbleConferenceStatus.ENDED;
            // webConferenceSession.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE, { bubbleConference: webConferenceSession, status: BubbleConferenceStatus.ENDED });

            //the conference no longer exists for this bubble, clear all data
            if (webConferenceSession.bubble.isBubbleConferenceStarted()) {
                webConferenceSession.bubble.conference.sessions = [];
            }
            webConferenceSession.bubble.sendEvent(BubbleEvents.ON_CONFERENCE_STOP_EVENT);

            return false;
        }

        return true;
    }

    public async dropAudio() {
        // drop le track audio
        const stream = await this.webrtcConfigAndUtils.getBrowserMedia(["audio"]);
        const audioTracks = stream.getAudioTracks();
        audioTracks[0].stop();
        audioTracks[0].dispatchEvent(new Event("ended"));
    }

    public waitWebConferenceConnection(session: BubbleConference): Promise<void> {
        return new Promise<void>((resolve) => {
            if (session.status === BubbleConferenceStatus.CONNECTED) { resolve(); return; }
            const subscription = this.eventSubscribe((event: RBEvent) => {
                if (event.data.status === BubbleConferenceStatus.CONNECTED) {
                    subscription?.unsubscribe();
                    resolve();
                }
            }, BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE);
        });
    }

    /**
     * Raise your hand in the concerned Conference
     * @param webConferenceSession - The conference session where the user is going to lower their hand
     */
    public async raiseHand(webConferenceSession: WebConferenceSession): Promise<WebConferenceSession> {
        try {
            const user = webConferenceSession.localParticipant;
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${user.id}/raise-hand`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info(`[WebConferenceService] raiseHand (raising my hand) -- ${webConferenceSession.bubble.dbId} -- success`);
            webConferenceSession.localParticipant.raiseHand = true;
            webConferenceSession.localParticipant.raiseHandDate = new Date();
            webConferenceSession.updateRaiseHandParticipants();
            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id, participant: user, action: "raise-hand" });
            return webConferenceSession;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "raiseHand");
            throw errorInfo.error;
        }
    }

    /**
     * Lower your hand in the concerned conference (you can also lower the hand of someone else if you are a Bubble Organizer)
     * @param webConferenceSession - The conference session where the user is going to lower their hand
     * @param user - The user lowering their hand
     */
    public async lowerHand(webConferenceSession: WebConferenceSession, user: BubbleConferenceParticipant): Promise<WebConferenceSession> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${user.id}/lower-hand`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
            this.logger.info(`[WebConferenceService] lowerHand -- ${webConferenceSession.id}) -- success`);
            user.raiseHand = false;
            user.raiseHandDate = null;
            webConferenceSession.updateRaiseHandParticipants();
            webConferenceSession.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_LIST_CHANGE);
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_LIST_UPDATE, { roomDbId: webConferenceSession.id });
            this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PARTICIPANT_UPDATE, { roomDbId: webConferenceSession.id, participant: user, action: "lower-hand" });
            return webConferenceSession;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "lowerHand");
            throw errorInfo.error;
        }
    }

    /**
     * Get all conference recaps that belong to the connected user
     */
    public async getRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]> {
        return this.bubbleConferenceRecapService.getRecaps(bubble);
    }

    /**
     * Get conference recaps shared with the connected user
     */
    public async getSharedWithMeRecaps(bubble?: Bubble): Promise<BubbleConferenceRecap[]> {
        return this.bubbleConferenceRecapService.getSharedWithMeRecaps(bubble);
    }

    public disableIncomingSharing(webConferenceSession: WebConferenceSession, disable: boolean) {
        if (!webConferenceSession?.haveJoined) { return; }
        if (webConferenceSession.disableIncomingSharing === disable) { return; }

        webConferenceSession.disableIncomingSharing = disable;
        if (webConferenceSession.getRemoteSharingParticipant()) {
            webConferenceSession.getRemoteSharingSession()?.sendHold(disable);
            if (disable) {
                // webConferenceSession.updatePinnedParticipant(null);
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                    mediaType: BubbleConferenceMedia.SHARING,
                    action: BubbleConferenceMediaAction.REMOVED
                });
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.REMOVED });
            }
            else {
                // webConferenceSession.updatePinnedParticipant(webConferenceSession.getSharingParticipant(), "sharing");
                webConferenceSession.sendEvent(BubbleConferenceEvents.ON_REMOTE_MEDIA_CHANGE, {
                    mediaType: BubbleConferenceMedia.SHARING,
                    action: BubbleConferenceMediaAction.ADDED,
                    participant: webConferenceSession.getSharingParticipant()
                });
                this.sendEvent(BubbleConferenceServiceEvents.ON_WEBCONFERENCE_REMOTEMEDIA_UPDATE, { roomDbId: webConferenceSession.id, mediaType: BubbleConferenceMedia.SHARING, action: BubbleConferenceMediaAction.ADDED });
            }
        }
    }

    public async updateVideoQualityForPublisher(conferenceId: string, quality: BubbleConferenceVideoQuality, publisher?: BubbleConferenceParticipant): Promise<void> {
        try {
            const webConferenceSession = this.getWebConferenceSessionById(conferenceId);
            if (!webConferenceSession) {
                throw new Error("[WebConferenceService] updateVideoQualityForPublisher -- missing bubble conference");
            }

            let videoSession = publisher?.videoSession;
            if (!publisher) {
                //check if we've dynamic feed
                videoSession = webConferenceSession.dynamicFeedSession;
            }

            if (!videoSession) {
                throw new Error("[WebConferenceService] updateVideoQualityForPublisher -- no video session for this publisher");
            }

            const subStreamLevel = quality;
            publisher.videoElementSizeSubstreamLevel = quality;

            if (!publisher) {
                //dynamic feed update
                if (!webConferenceSession.dynamicFeedSession.subStreamLevel) webConferenceSession.dynamicFeedSession.subStreamLevel = 0;

                //update only if there's no quality limitation on the substream level
                if (subStreamLevel !== webConferenceSession.dynamicFeedSession.subStreamLevel) {
                    webConferenceSession.dynamicFeedSession.subStreamLevel = subStreamLevel;
                    this.logger.info(`[WebConferenceService] updateVideoQualityForPublisher -- dynamic feed -- ${conferenceId} -- ${subStreamLevel}`);
                    const params = { option: "update", subStreamLevel: subStreamLevel };
                    const body = JSON.stringify(params);
                    const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${webConferenceSession.localParticipant.id}`;
                    const headers = this.authService.getPostHeader();
                    const response = await fetch(url, { method: 'PUT', headers, body });
                    if (!response.ok) throw response;
                }
            }
            else {
                if (subStreamLevel !== publisher.subStreamLevel && subStreamLevel <= publisher.quailityLimitationSubstreamLevel) {
                    publisher.subStreamLevel = subStreamLevel;
                    this.logger.info(`[WebConferenceService] updateVideoQualityForPublisher -- ${conferenceId} -- ${publisher.id} -- ${subStreamLevel}`);
                    const params = { option: "update", subStreamLevel: subStreamLevel, publisherId: publisher.id };
                    const body = JSON.stringify(params);
                    const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${webConferenceSession.localParticipant.id}`;
                    const headers = this.authService.getPostHeader();
                    const response = await fetch(url, { method: 'PUT', headers, body });
                    if (!response.ok) throw response;
                }
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateVideoQualityForPublisher');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async startLiveTranscription(webConference: WebConferenceSession, translationLanguage: string = '') {
        try {
            const data = translationLanguage ? { translationLanguage } : {};
            this.logger.info(`[WebConferenceService] startLiveTranscription (source: ${webConference.id}) with translationLanguage: ${translationLanguage}`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConference.id}/start-live-transcription`;
            const body = JSON.stringify(data);
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) throw response;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "startLiveTranscription");
            this.logger.error(`[WebConferenceService] ${errorInfo.error.message}`);
            throw (errorInfo.error);
        }
    }

    public async stopLiveTranscription(webConference: WebConferenceSession) {
        try {
            this.logger.info(`[WebConferenceService] stopLiveTranscription (source: ${webConference.id})`);
            const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConference.id}/stop-live-transcription`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: 'PUT', headers });
            if (!response.ok) throw response;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "stopLiveTranscription");
            this.logger.error(`[WebConferenceService] ${errorInfo.error.message}`);
            throw (errorInfo.error);
        }
    }
}
